import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Wallet, AlertTriangle, TrendingDown, TrendingUp, Activity, Info, PieChart,
  ArrowUpRight, FileText, BarChart2, Target, RefreshCw, HelpCircle, Sparkles,
  Zap, Shield, Lightbulb, Globe, X, LineChart, Radar, ChevronDown, Star,
  LayoutList, LayoutDashboard, BarChart3, Database, Calculator, Settings, Lock, Crown, ArrowRight,
  Maximize2, Table, CheckCircle2, Layers
} from 'lucide-react';
import { LiveMarketIndicatorsModal } from './LiveMarketIndicatorsModal';
import { ResponsiveContainer, PieChart as RePieChart, Pie, Cell, Tooltip } from 'recharts';
import { MacroState, Holding, MarketSignal, MacroIndicators, UserProfile } from '../types';
import { useCollapsibleState } from '../hooks/useCollapsibleState';
import { AIErrorInfo } from '../utils/aiErrorHandler';
import { MacroTrendPopover } from './MacroTrendPopover';
import { getMarketStatus, getMarketStatusColor, formatCurrency } from '../utils/market';
import { auth } from '../firebase';
import { checkIfAdmin, syncAllBrokerages, fetchPendingOrders, fetchLsOptionsChain, fetchLsShortSelling } from '../services/api';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { RefreshCcw } from 'lucide-react';
import { MacroRegimesCard } from './MacroRegimesCard';
export { MacroRegimesCard };
import { RegimeBadge } from './CommonUI';
import { DASHBOARD_CONFIG } from '../constants/dashboard';
import { AssetAllocationCard } from './PortfolioCards';
import { useAppConfig } from '../hooks/useAppConfig';


export const TooltipHelp = ({
  text,
  direction = 'up',
}: {
  text: string;
  direction?: 'up' | 'down';
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        x: Math.min(rect.left + rect.width / 2, window.innerWidth - 170),
        y: direction === 'up' ? rect.top - 8 : rect.bottom + 8,
      });
    }
    setVisible(true);
  }, [direction]);

  const handleMouseLeave = useCallback(() => setVisible(false), []);

  return (
    <div
      ref={triggerRef}
      className="relative flex items-center ml-1"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <HelpCircle className="w-4 h-4 text-white/30 cursor-help hover:text-white/80 transition-colors" />
      {visible && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            left: coords.x,
            ...(direction === 'up'
              ? { bottom: `${window.innerHeight - coords.y}px` }
              : { top: coords.y }),
            transform: 'translateX(-50%)',
            zIndex: 99999,
          }}
          className="w-max max-w-[300px] p-3 bg-[#0a0a0a] border border-white/20 rounded-xl text-[11px] leading-relaxed text-white/90 shadow-[0_8px_32px_rgba(0,0,0,0.7)] pointer-events-none whitespace-pre-wrap break-words"
        >
          {text}
          <div
            className={[
              'absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0a0a0a] rotate-45',
              direction === 'up'
                ? '-bottom-1 border-r border-b border-white/20'
                : '-top-1 border-l border-t border-white/20',
            ].join(' ')}
          />
        </div>,
        document.body,
      )}
    </div>
  );
};

export function AccountSelector({
  accounts,
  selectedAccountId,
  onSelectAccountId,
  language = 'ko'
}: {
  accounts: any[];
  selectedAccountId: string;
  onSelectAccountId: (id: string) => void;
  language?: string;
}) {
  const getProviderIcon = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'kiwoom': return '🔴';
      case 'koreainvest': return '🟢';
      case 'manual': return '✏️';
      default: return '🏦';
    }
  };

  const getProviderName = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'kiwoom': return '키움증권';
      case 'koreainvest': return '한국투자증권';
      case 'manual': return '수동 기입';
      default: return provider;
    }
  };

  return (
    <div className="relative flex items-center bg-black/40 border border-white/10 rounded-xl p-1.5 shadow-inner backdrop-blur-md">
      <span className="text-[10px] text-white/40 font-black uppercase tracking-widest px-2.5">
        {language === 'ko' ? '조회 계좌' : 'Account'}
      </span>
      <select
        value={selectedAccountId}
        onChange={(e) => onSelectAccountId(e.target.value)}
        className="bg-transparent text-xs font-bold text-white pr-8 pl-1.5 py-1 focus:outline-none cursor-pointer appearance-none relative z-10 border-0"
        style={{
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 4px center',
          backgroundSize: '16px'
        }}
      >
        <option value="all" className="bg-[#0a0a0a] text-white">
          {language === 'ko' ? '전체 통합 계좌 🌐' : 'Unified Accounts 🌐'}
        </option>
        {accounts.map(acc => (
          <option key={acc.id} value={acc.id} className="bg-[#0a0a0a] text-white">
            {getProviderIcon(acc.provider)} {acc.account_name || getProviderName(acc.provider)} ({acc.account_num})
          </option>
        ))}
      </select>
    </div>
  );
}

interface MarqueeItemData {
  label: string;
  value: string;
  change?: number;
  isMock?: boolean;
}

const MarqueeItem = ({ item, color = 'blue' }: { item: MarqueeItemData, color?: 'blue' | 'yellow' | 'purple' }) => {
  const theme = {
    blue: { bg: 'bg-blue-500/5', text: 'text-blue-50', minW: 'min-w-[160px]' },
    yellow: { bg: 'bg-yellow-500/5', text: 'text-yellow-50', minW: 'min-w-[160px]' },
    purple: { bg: 'bg-purple-500/5', text: 'text-purple-50', minW: 'min-w-[170px]' }
  }[color];

  return (
    <div className={`flex flex-col items-center justify-center gap-1 bg-white/5 border border-white/10 px-6 py-4 rounded-3xl shrink-0 shadow-lg hover:border-white/20 transition-all cursor-default relative overflow-hidden ${theme.minW}`}>
      <div className={`absolute inset-0 ${theme.bg} pointer-events-none`} />
      <span className="text-white/40 font-bold uppercase tracking-widest text-[10px] relative z-10">{item.label}</span>
      <div className="flex items-baseline gap-2 relative z-10">
        <span className={`font-black ${theme.text} tracking-tight text-lg`}>{item.value}</span>
        {item.change !== undefined && (
          <span className={`text-xs font-bold ${item.change > 0 ? 'text-emerald-400' : item.change < 0 ? 'text-red-400' : 'text-white/60'}`}>
            {item.change > 0 ? '+' : ''}{item.change.toFixed(2)}%
          </span>
        )}
      </div>
    </div>
  );
};

// 1.8 Macro Indicator Summary Card (MMO-068)
export function MacroIndicatorSummaryCard({
  macro,
  indicators,
  language = 'ko',
  marketMode = 'US'
}: {
  macro: MacroState,
  indicators?: MacroIndicators | null,
  language?: string,
  marketMode?: 'US' | 'KR'
}) {
  const lang = language || 'ko';
  const market = marketMode || 'US';

  // Calculate update status
  const getStatus = (date?: string) => {
    if (!date) return 'offline';
    const diff = Date.now() - new Date(date).getTime();
    if (diff < 24 * 60 * 60 * 1000) return 'live';
    if (diff < 7 * 24 * 60 * 60 * 1000) return 'recent';
    return 'stale';
  };

  const sources = [
    { name: 'FRED (US Econ)', status: getStatus(indicators?.economics?.pmiManufacturing?.date) },
    { name: 'Yahoo (Market)', status: getStatus(indicators?.sp500?.date) },
    { name: market === 'KR' ? 'BOK (ECOS)' : 'Fed (Reserve)', status: getStatus(indicators?.fedFunds?.date) },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live': return 'bg-emerald-500';
      case 'recent': return 'bg-blue-400';
      case 'stale': return 'bg-amber-400';
      default: return 'bg-white/10';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass-premium rounded-[2.5rem] p-8 border border-white/10 bg-gradient-to-br from-indigo-500/10 via-transparent to-purple-500/10 relative overflow-hidden group"
    >
      <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[100px] -mr-32 -mt-32 animate-pulse" />

      <div className="relative z-10 flex flex-col md:flex-row gap-8">
        {/* Left: Primary Regime Status */}
        <div className="flex-1 space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-500/20 rounded-2xl border border-indigo-500/30">
              <Activity className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-sm font-black text-white/40 uppercase tracking-widest">{lang === 'ko' ? '시장 국면 분석' : 'MARKET REGIME'}</h3>
              <div className="flex items-center gap-3 mt-1">
                <RegimeBadge regime={macro.marketRegime} lang={lang as 'ko' | 'en'} />
                <span className="text-white/20 font-bold">|</span>
                <span className="text-xs font-bold text-white/60">{lang === 'ko' ? '초정밀 매크로 엔진' : 'High-Precision Engine'}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
              <p className="text-[10px] font-bold text-white/30 uppercase mb-1">{lang === 'ko' ? '거시 리스크' : 'MACRO RISK'}</p>
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-black ${macro.riskMetrics?.riskScore >= 60 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {macro.riskMetrics?.riskScore || 0}
                </span>
                <span className="text-[10px] font-bold text-white/20">/ 100</span>
              </div>
            </div>
            <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
              <p className="text-[10px] font-bold text-white/30 uppercase mb-1">{lang === 'ko' ? '연준 스탠스' : 'FED SENTIMENT'}</p>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black text-purple-400">
                  {macro.fedFunds?.value != null ? (macro.fedFunds.value > 5.25 ? (lang === 'ko' ? '긴축' : 'Tight') : (lang === 'ko' ? '완화' : 'Easy')) : (lang === 'ko' ? '중립' : 'Neutral')}
                </span>
                <Zap className="w-3 h-3 text-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Right: Source Status */}
        <div className="w-full md:w-64 space-y-4">
          <h4 className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-4">{lang === 'ko' ? '데이터 동기화 상태' : 'DATA SYNC STATUS'}</h4>
          <div className="space-y-3">
            {sources.map(source => (
              <div key={source.name} className="flex items-center justify-between group/source">
                <div className="flex items-center gap-3">
                  <div className={`w-1.5 h-1.5 rounded-full ${getStatusColor(source.status)} shadow-[0_0_8px_currentColor]`} />
                  <span className="text-xs font-bold text-white/60 group-hover/source:text-white transition-colors">{source.name}</span>
                </div>
                <span className="text-[10px] font-mono font-bold text-white/20 uppercase">{source.status}</span>
              </div>
            ))}
          </div>

          <div className="pt-4 mt-4 border-t border-white/5">
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-white/30 font-bold uppercase">{lang === 'ko' ? '최종 업데이트' : 'LAST SYNC'}</span>
              <span className="text-white/60 font-mono">{macro.asOfDate || '-'}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}


// 1. Stats Grid
export function StatsGrid({
  totalValue,
  riskScore,
  indicators,
  dailyReturnAmt,
  dailyReturnPct,
  language = 'ko'
}: {
  totalValue: number,
  riskScore: number,
  indicators?: MacroIndicators | null,
  dailyReturnAmt?: number,
  dailyReturnPct?: number,
  language?: string
}) {
  const lang = language || 'ko';
  const { thresholds } = useAppConfig();
  
  const riskHigh = thresholds?.RISK_HIGH_THRESHOLD ?? DASHBOARD_CONFIG.RISK_HIGH_THRESHOLD;
  const riskMedium = thresholds?.RISK_MEDIUM_THRESHOLD ?? DASHBOARD_CONFIG.RISK_MEDIUM_THRESHOLD;

  const getRiskScoreColor = (score: number) => {
    if (score >= riskHigh) return 'text-red-400';
    if (score >= riskMedium) return 'text-orange-400';
    return 'text-emerald-400';
  };

  const getRiskStatus = (score: number) => {
    if (score >= riskHigh) return lang === 'ko' ? '위험' : 'High';
    if (score >= riskMedium) return lang === 'ko' ? '주의' : 'Elevated';
    return lang === 'ko' ? '안정' : 'Stable';
  };

  const stats = [
    {
      label: lang === 'ko' ? '총 포트폴리오 가치' : 'Total Portfolio Value',
      title: lang === 'ko' ? '현재 포트폴리오의 총 평가 금액 및 일간 수익률입니다.' : 'Current total portfolio evaluation and daily return.',
      value: `$${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: dailyReturnAmt !== undefined && dailyReturnPct !== undefined
        ? `${dailyReturnAmt >= 0 ? '+' : ''}$${Math.abs(dailyReturnAmt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${dailyReturnPct >= 0 ? '+' : ''}${dailyReturnPct.toFixed(2)}%)`
        : '',
      icon: Wallet
    },
    {
      label: lang === 'ko' ? '매크로 리스크 점수' : 'Macro Risk Score',
      title: lang === 'ko' ? '유동성, 신용, 정책 등 거시경제 상황을 종합 산출한 시장 위험 지수입니다. (100에 가까울수록 위험)' : 'A market risk index synthesized from liquidity, credit, and policy conditions. (Closer to 100 is higher risk)',
      value: riskScore,
      change: getRiskStatus(riskScore),
      icon: AlertTriangle,
      color: getRiskScoreColor(riskScore)
    },
  ];

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {stats.map((stat, i) => (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
          key={stat.label}
          className="bg-white/5 border border-white/10 p-6 rounded-2xl hover:bg-white/[0.07] transition-all group"
        >
          <div className="flex justify-between items-start mb-4">
            <div className="p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-colors">
              <stat.icon className="w-5 h-5 text-emerald-400" />
            </div>
            {stat.change && (
              <span className={`text-xs font-medium px-2 py-1 rounded-md bg-white/5 ${stat.change.startsWith('+') ? 'text-emerald-400' : stat.change.startsWith('-') ? 'text-red-400' : 'text-white/60'}`}>
                {stat.change}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mb-1">
            <p className="text-white/40 text-sm">{stat.label}</p>
            {stat.title && <TooltipHelp text={stat.title} />}
          </div>
          <h3 className={`text-2xl font-bold ${stat.color || ''}`}>{stat.value}</h3>
        </motion.div>
      ))}
    </section>
  );
}

// 1.5 Commodities Marquee
export function CommoditiesMarquee({ indicators, marketMode = 'US' }: { indicators?: MacroIndicators | null, marketMode?: 'US' | 'KR' }) {
  const { marquee } = useAppConfig();
  if (!indicators) return null;

  const isKr = marketMode === 'KR';
  
  // Key mapping for commodity aliases
  const COMMODITY_KEY_ALIAS: Record<string, string> = {
    'wti': 'oil',
    'crude': 'oil',
    'crudeOil': 'oil',
    'btc': 'bitcoin',
  };

  // Use DB config if available, fallback to default hardcoded logic
  const dbItems = marquee.filter(m => m.market === marketMode && m.category === 'commodity');
  
  const items = dbItems.length > 0 ? dbItems.map(m => {
    const rawKey = m.target_indicator_key;
    const resolvedKey = COMMODITY_KEY_ALIAS[rawKey] || rawKey;
    
    // flat 접근 → futures 중첩 → government 중첩 순서로 fallback
    const indData: any = (indicators as any)[resolvedKey]
      ?? (indicators as any)[rawKey]
      ?? (indicators.futures as any)?.[resolvedKey]
      ?? (indicators.futures as any)?.[rawKey]
      ?? (indicators.government as any)?.[resolvedKey]
      ?? (indicators.government as any)?.[rawKey]
      ?? null;
    const val = indData?.value ?? 0;
    
    // Formatting logic based on resolvedKey
    let formattedVal = '...';
    if (indData && indData.value !== undefined && indData.value !== null) {
      if (resolvedKey === 'bitcoin') {
        formattedVal = `$${val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      } else if (['gold', 'silver', 'copper', 'oil', 'naturalGas'].includes(resolvedKey)) {
        formattedVal = `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      } else if (['ironOre', 'lithium', 'wheat', 'corn', 'soybeans'].includes(resolvedKey)) {
        formattedVal = `$${val.toFixed(2)}`;
      } else if (['nickel', 'cobalt'].includes(resolvedKey)) {
        formattedVal = `$${val.toLocaleString()}`;
      } else if (resolvedKey === 'bdi') {
        formattedVal = `${val.toLocaleString()} pts`;
      } else {
        formattedVal = `${val}`;
      }
    }
    return { label: m.label, value: formattedVal, change: indData?.change, isMock: indicators.isMock };
  }) : isKr
    ? [
      { label: 'Gold (금)', value: indicators.gold ? `$${(indicators.gold.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.gold?.change, isMock: indicators.isMock },
      { label: 'Silver (은)', value: indicators.silver ? `$${(indicators.silver.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.silver?.change, isMock: indicators.isMock },
      { label: 'Copper (구리)', value: indicators.copper ? `$${(indicators.copper.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.copper?.change, isMock: indicators.isMock },
      { label: 'WTI Crude Oil (WTI유)', value: indicators.oil ? `$${(indicators.oil.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.oil?.change, isMock: indicators.isMock },
      { label: 'Natural Gas (천연가스)', value: indicators.naturalGas ? `$${(indicators.naturalGas.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.naturalGas?.change, isMock: indicators.isMock },
      { label: 'Iron Ore (철광석)', value: indicators.ironOre ? `$${(indicators.ironOre.value ?? 0).toFixed(2)}` : '...', change: indicators.ironOre?.change, isMock: indicators.isMock },
      { label: 'Lithium (리튬)', value: indicators.lithium ? `$${(indicators.lithium.value ?? 0).toFixed(2)}` : '...', change: indicators.lithium?.change, isMock: indicators.isMock },
      { label: 'Nickel (니켈)', value: indicators.nickel ? `$${(indicators.nickel.value ?? 0).toLocaleString()}` : '...', change: indicators.nickel?.change, isMock: indicators.isMock },
      { label: 'Cobalt (코발트)', value: indicators.cobalt ? `$${(indicators.cobalt.value ?? 0).toLocaleString()}` : '...', change: indicators.cobalt?.change, isMock: indicators.isMock },
      { label: 'Wheat (밀)', value: indicators.wheat ? `$${(indicators.wheat.value ?? 0).toFixed(2)}` : '...', change: indicators.wheat?.change, isMock: indicators.isMock },
      { label: 'Corn (옥수수)', value: indicators.corn ? `$${(indicators.corn.value ?? 0).toFixed(2)}` : '...', change: indicators.corn?.change, isMock: indicators.isMock },
      { label: 'Soybeans (대두)', value: indicators.soybeans ? `$${(indicators.soybeans.value ?? 0).toFixed(2)}` : '...', change: indicators.soybeans?.change, isMock: indicators.isMock },
      { label: 'BDI (해운운임지수)', value: indicators.bdi ? `${(indicators.bdi.value ?? 0).toLocaleString()} pts` : '...', change: indicators.bdi?.change, isMock: indicators.isMock },
      { label: 'Bitcoin (비트코인)', value: indicators.bitcoin ? `$${(indicators.bitcoin.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '...', change: indicators.bitcoin?.change, isMock: indicators.isMock },
    ]
    : [
      { label: 'Gold', value: indicators.gold ? `$${(indicators.gold.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.gold?.change, isMock: indicators.isMock },
      { label: 'WTI Crude', value: indicators.oil ? `$${(indicators.oil.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.oil?.change, isMock: indicators.isMock },
      { label: 'Copper', value: indicators.copper ? `$${(indicators.copper.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.copper?.change, isMock: indicators.isMock },
      { label: 'Silver', value: indicators.silver ? `$${(indicators.silver.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.silver?.change, isMock: indicators.isMock },
      { label: 'Natural Gas', value: indicators.naturalGas ? `$${(indicators.naturalGas.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '...', change: indicators.naturalGas?.change, isMock: indicators.isMock },
      { label: 'Bitcoin', value: indicators.bitcoin ? `$${(indicators.bitcoin.value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '...', change: indicators.bitcoin?.change, isMock: indicators.isMock },
    ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full overflow-hidden bg-black/40 border border-white/10 py-4 pause-marquee rounded-3xl relative"
    >
      <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none rounded-l-3xl" />
      <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none rounded-r-3xl" />

      <div className="flex w-max animate-marquee gap-6 pl-6" style={{ animationDuration: '60s' }}>
        {/* Double the items for seamless animation loop (translateX -50%) */}
        {[...items, ...items, ...items, ...items, ...items].map((item, idx) => (
          <MarqueeItem key={`${item.label}-${idx}`} item={item} color="yellow" />
        ))}
      </div>
    </motion.div>
  );
}

// 1.6 Indices Marquee
export function IndicesMarquee({ indicators, marketMode = 'US' }: { indicators?: MacroIndicators | null, marketMode?: 'US' | 'KR' }) {
  const { marquee } = useAppConfig();
  if (!indicators) return null;
  
  const INDEX_KEY_ALIAS: Record<string, string> = {
    'dow': 'djia',
    'dji': 'djia',
    'us10y': 'treasury10Y',
    'us2y':  'treasury2Y',
    'kr10y': 'krTreasury10Y',
  };

  const dbItems = marquee.filter(m => m.market === marketMode && m.category === 'index');
  
  const items = dbItems.length > 0 ? dbItems.map(m => {
    const rawKey = m.target_indicator_key;
    const resolvedKey = INDEX_KEY_ALIAS[rawKey] || rawKey;

    // flat 접근 → government 중첩 → futures 중첩 순서로 fallback
    const indData: any = (indicators as any)[resolvedKey]
      ?? (indicators as any)[rawKey]
      ?? (indicators.government as any)?.[resolvedKey]
      ?? (indicators.government as any)?.[rawKey]
      ?? (indicators.futures as any)?.[resolvedKey]
      ?? (indicators.futures as any)?.[rawKey]
      ?? null;
    const val = indData?.value ?? 0;
    
    let formattedVal = '...';
    if (indData && indData.value !== undefined && indData.value !== null) {
      if (['sp500', 'nasdaq', 'sox', 'kospi', 'kosdaq', 'usdkrw', 'djia', 'dow', 'russell2000'].includes(resolvedKey)) {
        formattedVal = val.toLocaleString(undefined, { minimumFractionDigits: 2 });
      } else if (['us10y', 'kr10y', 'yieldSpread', 'treasury10Y', 'krTreasury10Y', 'treasury2Y'].includes(resolvedKey) || ['us10y', 'kr10y'].includes(rawKey)) {
        formattedVal = val.toFixed(2) + '%';
      } else if (['fearGreed', 'kospiFgi'].includes(resolvedKey)) {
        formattedVal = val.toFixed(0);
      } else {
        formattedVal = val.toFixed(2);
      }
    }
    return { label: m.label, value: formattedVal, change: indData?.change, isMock: indicators.isMock };
  }) : marketMode === 'KR'
    ? [
      { label: 'KOSPI', value: indicators.kospi && indicators.kospi.value ? (indicators.kospi.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.kospi?.change, isMock: indicators.isMock },
      { label: 'KOSDAQ', value: indicators.kosdaq && indicators.kosdaq.value ? (indicators.kosdaq.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.kosdaq?.change, isMock: indicators.isMock },
      { label: 'KOSPI FGI', value: indicators.kospiFgi && indicators.kospiFgi.value ? (indicators.kospiFgi.value).toFixed(0) : '...', change: indicators.kospiFgi?.change, isMock: indicators.isMock },
      { label: 'SOX Index', value: indicators.sox && indicators.sox.value ? (indicators.sox.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.sox?.change, isMock: indicators.isMock },
      { label: 'USD/KRW', value: indicators.usdkrw && indicators.usdkrw.value ? (indicators.usdkrw.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.usdkrw?.change, isMock: indicators.isMock },
      { label: 'V-KOSPI', value: indicators.vkospi && indicators.vkospi.value ? (indicators.vkospi.value).toFixed(2) : '...', change: indicators.vkospi?.change, isMock: indicators.isMock },
      { label: '10Y Yield (KR)', value: indicators.government?.krTreasury10Y && indicators.government.krTreasury10Y.value ? (indicators.government.krTreasury10Y.value).toFixed(2) + '%' : '...', change: indicators.government?.krTreasury10Y?.change, isMock: indicators.isMock }
    ]
    : [
      { label: 'S&P 500', value: indicators.sp500 && indicators.sp500.value ? (indicators.sp500.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.sp500?.change, isMock: indicators.isMock },
      { label: 'Nasdaq 100', value: indicators.nasdaq && indicators.nasdaq.value ? (indicators.nasdaq.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.nasdaq?.change, isMock: indicators.isMock },
      { label: 'DOW JONES', value: indicators.djia && indicators.djia.value ? (indicators.djia.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.djia?.change, isMock: indicators.isMock },
      { label: 'SOX Index', value: indicators.sox && indicators.sox.value ? (indicators.sox.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.sox?.change, isMock: indicators.isMock },
      { label: 'DXY Index', value: indicators.dxy && indicators.dxy.value ? (indicators.dxy.value).toFixed(2) : '...', change: indicators.dxy?.change, isMock: indicators.isMock },
      { label: 'Yield Spread (10y-2y)', value: indicators.yieldSpread && indicators.yieldSpread.value ? (indicators.yieldSpread.value).toFixed(2) + '%' : '...', change: indicators.yieldSpread?.change, isMock: indicators.isMock },
      { label: 'Fear & Greed', value: indicators.fearGreed && indicators.fearGreed.value ? (indicators.fearGreed.value).toFixed(0) : (indicators.vix && indicators.vix.value ? (indicators.vix.value).toFixed(1) : '...'), change: indicators.fearGreed?.change ?? indicators.vix?.change, isMock: indicators.isMock }
    ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full overflow-hidden bg-black/40 border border-white/10 py-4 pause-marquee rounded-3xl relative"
    >
      <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none rounded-l-3xl" />
      <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none rounded-r-3xl" />

      <div className="flex w-max animate-marquee gap-6 pl-6" style={{ animationDuration: '60s', animationDirection: 'reverse' }}>
        {/* Double the items for seamless animation loop (translateX -50%) */}
        {[...items, ...items, ...items, ...items, ...items].map((item, idx) => (
          <MarqueeItem key={`${item.label}-${idx}`} item={item} color="blue" />
        ))}
      </div>
    </motion.div>
  );
}

// 1.7 Futures Marquee
export function FuturesMarquee({ indicators, marketMode = 'US' }: { indicators?: MacroIndicators | null, marketMode?: 'US' | 'KR' }) {
  const { marquee } = useAppConfig();
  if (!indicators || !indicators.futures) return null;

  const isKr = marketMode === 'KR';

  const FUTURES_KEY_ALIAS: Record<string, string> = {
    'sp500_fut': 'sp500',
    'nasdaq_fut': 'nasdaq',
    'dow_fut': 'dow',
    'russell_fut': 'russell',
    'treasury10y_fut': 'treasury10y',
    'kospi_fut': 'krx200',
    'krx200_fut': 'krx200',
    'kosdaq_fut': 'kosdaq',
    'usdkrw_fut': 'usdkrw',
  };

  const dbItems = marquee.filter(m => m.market === marketMode && m.category === 'futures');
  
  const items = dbItems.length > 0 ? dbItems.map(m => {
    const rawKey = m.target_indicator_key;
    const resolvedKey = FUTURES_KEY_ALIAS[rawKey] || rawKey;

    // futures are nested in indicators.futures, but some might be direct indicator
    const indData: any = (indicators.futures as any)?.[resolvedKey] 
      ?? (indicators.futures as any)?.[rawKey]
      ?? (indicators as any)[resolvedKey] 
      ?? (indicators as any)[rawKey];
    const val = indData?.value ?? 0;
    
    let formattedVal = '...';
    if (indData && indData.value !== undefined && indData.value !== null) {
      if (['sp500', 'nasdaq', 'dow', 'russell', 'krx200', 'kospi', 'kosdaq'].includes(resolvedKey)) {
        formattedVal = val.toLocaleString(undefined, { minimumFractionDigits: 2 });
      } else if (resolvedKey === 'brent') {
        formattedVal = `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      } else if (resolvedKey === 'usdkrw') {
        formattedVal = val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      } else {
        formattedVal = val.toFixed(2);
      }
    }
    return { label: m.label, value: formattedVal, change: indData?.change, isMock: indicators.isMock };
  }) : isKr
    ? [
      { label: 'KOSPI 200 Futures', value: indicators.futures.krx200 && indicators.futures.krx200.value ? (indicators.futures.krx200.value).toFixed(2) : '...', change: indicators.futures.krx200?.change, isMock: indicators.isMock },
      { label: 'S&P 500 Fut', value: indicators.futures.sp500 && indicators.futures.sp500.value ? (indicators.futures.sp500.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.futures.sp500?.change, isMock: indicators.isMock },
      { label: 'Nasdaq 100 Fut', value: indicators.futures.nasdaq && indicators.futures.nasdaq.value ? (indicators.futures.nasdaq.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.futures.nasdaq?.change, isMock: indicators.isMock },
      { label: 'USD/KRW Fut', value: indicators.usdkrw && indicators.usdkrw.value ? (indicators.usdkrw.value).toFixed(1) : '...', change: indicators.usdkrw?.change, isMock: indicators.isMock }
    ]
    : [
      { label: 'S&P 500 Futures', value: indicators.futures.sp500 && indicators.futures.sp500.value ? (indicators.futures.sp500.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.futures.sp500?.change, isMock: indicators.isMock },
      { label: 'Nasdaq 100 Futures', value: indicators.futures.nasdaq && indicators.futures.nasdaq.value ? (indicators.futures.nasdaq.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.futures.nasdaq?.change, isMock: indicators.isMock },
      { label: 'Dow Jones Futures', value: indicators.futures.dow && indicators.futures.dow.value ? (indicators.futures.dow.value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '...', change: indicators.futures.dow?.change, isMock: indicators.isMock },
      { label: '10Y T-Note Futures', value: indicators.futures.treasury10y && indicators.futures.treasury10y.value ? (indicators.futures.treasury10y.value).toFixed(2) : '...', change: indicators.futures.treasury10y?.change, isMock: indicators.isMock }
    ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full overflow-hidden bg-black/40 border border-white/10 py-4 pause-marquee rounded-3xl relative mt-2"
    >
      <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none rounded-l-3xl" />
      <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none rounded-r-3xl" />

      <div className="flex w-max animate-marquee gap-6 pl-6" style={{ animationDuration: '60s' }}>
        {/* Double the items for seamless animation loop (translateX -50%) */}
        {[...items, ...items, ...items, ...items, ...items].map((item, idx) => (
          <MarqueeItem key={`${item.label}-${idx}`} item={item} color="purple" />
        ))}
      </div>
    </motion.div>
  );
}

// 1.7 Portfolio Summary
export function PortfolioSummary({
  totalValue,
  dailyReturnAmt,
  dailyReturnPct,
  totalReturnAmt,
  totalReturnPct,
  lastUpdated,
  targetGoalAmount,
  onNavigateToSettings,
  language = 'ko',
  marketMode = 'US',
  currencyPreference = 'USD',
  onCurrencyPreferenceChange,
  holdings
}: {
  totalValue: number,
  dailyReturnAmt: number,
  dailyReturnPct: number,
  totalReturnAmt: number,
  totalReturnPct: number,
  lastUpdated?: string,
  targetGoalAmount?: number,
  onNavigateToSettings?: () => void,
  language?: string,
  marketMode?: 'US' | 'KR' | 'ALL',
  currencyPreference?: 'USD' | 'KRW',
  onCurrencyPreferenceChange?: (pref: 'USD' | 'KRW') => void,
  holdings?: Holding[]
}) {
  const lang = language || 'ko';
  const market = marketMode || 'US';
  const marketStatus = getMarketStatus(market);
  const statusColorClass = getMarketStatusColor(marketStatus);

  // Respect currencyPreference for display
  const displayMarket = currencyPreference === 'KRW' ? 'KR' : 'US';

  return (
    <section className="space-y-4">
      <div className="flex justify-between items-center px-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full animate-pulse ${marketStatus !== 'Closed' ? 'bg-emerald-500' : 'bg-orange-500'}`} />
            <div className={`flex items-center gap-2 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-widest ${statusColorClass}`}>
              <Globe className="w-3 h-3" />
              {market === 'ALL' ? (
                lang === 'ko' ? '통합 포트폴리오' : 'Unified Portfolio'
              ) : (
                `${market === 'KR' ? (lang === 'ko' ? '한국 시장' : 'KR Market') : (lang === 'ko' ? '미국 시장' : 'US Market')} : ${marketStatus === 'Day market' ? (lang === 'ko' ? '정규장' : 'Day Market') :
                  marketStatus === 'Pre market' ? (lang === 'ko' ? '프리마켓' : 'Pre-Market') :
                    marketStatus === 'After market' ? (lang === 'ko' ? '애프터마켓' : 'After-Market') :
                      (lang === 'ko' ? '장마감' : 'Closed')
                }`
              )}
            </div>
          </div>
          {/* Currency Preference Selector */}
          <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 scale-90 sm:scale-100">
            {(['USD', 'KRW'] as const).map((pref) => (
              <button
                key={pref}
                onClick={() => onCurrencyPreferenceChange?.(pref)}
                className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all ${currencyPreference === pref
                  ? 'bg-purple-500/20 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.2)]'
                  : 'text-white/20 hover:text-white/40'
                  }`}
              >
                {pref}
              </button>
            ))}
          </div>
        </div>
        {lastUpdated && (
          <span className="text-[10px] text-white/20 font-mono italic">
            {lang === 'ko' ? '마지막 갱신' : 'Last Refresh'}: {new Date(lastUpdated).toLocaleTimeString()}
          </span>
        )}
      </div>
      <div className={`grid grid-cols-1 ${holdings ? 'lg:grid-cols-3' : 'md:grid-cols-3'} gap-6 relative z-10`}>
        {/* Total Portfolio Value */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 border border-white/10 p-6 rounded-3xl hover:bg-white/[0.07] bg-gradient-to-br from-blue-500/5 to-transparent transition-all group relative flex flex-col h-full"
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 blur-3xl -mr-16 -mt-16 pointer-events-none" />
          </div>
          <div className="flex justify-between items-start mb-2">
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em]">{lang === 'ko' ? '총 포트폴리오 가치' : 'Total Portfolio Value'}</p>
            <TooltipHelp text={lang === 'ko' ? "보유 자산의 실시간 합계 가치와 누적 수익률입니다." : "Real-time total value and cumulative performance of holdings."} />
          </div>
          <div className="flex items-baseline gap-3 mb-4">
            <h3 className="text-3xl font-black text-white tracking-tight group-hover:text-blue-400 transition-colors">
              {formatCurrency(totalValue, displayMarket)}
            </h3>
            <div className={`text-[11px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1 ${totalReturnPct >= 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
              {totalReturnPct >= 0 ? '▲' : '▼'} {Math.abs(totalReturnPct).toFixed(2)}%
            </div>
          </div>

          {/* Target Goal Progress */}
          {targetGoalAmount ? (
            <div className="space-y-1.5 border-t border-white/5 pt-4 mt-auto w-full">
              <div className="flex justify-between items-center px-0.5">
                <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest flex items-center gap-1">
                  <Target className="w-3 h-3 text-purple-400" />
                  {lang === 'ko' ? '목표 달성률' : 'Goal Progress'}
                </span>
                <span className="text-[10px] font-mono text-purple-400 font-bold">
                  {Math.min((totalValue / targetGoalAmount) * 100, 100).toFixed(1)}%
                </span>
              </div>
              <div className="h-1.5 w-full bg-black/40 rounded-full overflow-hidden border border-white/5">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min((totalValue / targetGoalAmount) * 100, 100)}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-purple-500 to-blue-400 rounded-full relative"
                >
                  <div className="absolute top-0 right-0 bottom-0 w-4 bg-white/20 blur-[2px]" />
                </motion.div>
              </div>
              <div className="flex justify-between items-center px-0.5 pt-0.5">
                <span className="text-[9px] text-white/30 truncate max-w-[120px]">
                  {lang === 'ko'
                    ? `남은 금액: ${formatCurrency(Math.max(targetGoalAmount - totalValue, 0), displayMarket, true)}`
                    : `Remaining: ${formatCurrency(Math.max(targetGoalAmount - totalValue, 0), displayMarket, true)}`}
                </span>
                <span className="text-[9px] text-white/30 ml-auto">
                  {lang === 'ko' ? '목표:' : 'Target:'} {formatCurrency(targetGoalAmount, displayMarket, true)}
                </span>
              </div>
            </div>
          ) : (
            <div className="border-t border-white/5 pt-4 mt-auto w-full flex items-center">
              <button
                onClick={onNavigateToSettings}
                className="text-[10px] w-full text-left text-purple-400/60 hover:text-purple-400 font-medium tracking-wide flex items-center gap-1 transition-colors group/hint"
              >
                <Target className="w-3 h-3 group-hover/hint:scale-110 transition-transform" />
                {lang === 'ko' ? '투자 목표 금액을 설정해보세요 →' : 'Set your financial goal →'}
              </button>
            </div>
          )}
        </motion.div>

        {/* Daily Return & Total Return Column */}
        {holdings ? (
          <div className="flex flex-col gap-6 h-full justify-between">
            {/* Daily Return */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/5 border border-white/10 p-6 rounded-3xl hover:bg-white/[0.07] bg-gradient-to-br from-emerald-500/5 to-transparent transition-all group relative flex-1 flex flex-col justify-center min-h-[120px]"
            >
              <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-3xl -mr-16 -mt-16 pointer-events-none" />
              </div>
              <div className="flex justify-between items-start mb-2">
                <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em]">{lang === 'ko' ? '일간 변동 손익' : 'Daily Return Amount'}</p>
                <TooltipHelp text={lang === 'ko' ? "직전 거래일 종가 대비 현재 자산 가치의 변동 금액과 수익률입니다." : "Change in asset value and return percentage compared to the previous close."} />
              </div>
              <div className="flex items-baseline gap-3">
                <h3 className={`text-3xl font-bold tracking-tighter ${dailyReturnAmt >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {dailyReturnAmt >= 0 ? '+' : ''}{formatCurrency(dailyReturnAmt, displayMarket)}
                </h3>
                <div className={`text-[11px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1 ${dailyReturnPct >= 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                  {dailyReturnPct >= 0 ? '▲' : '▼'} {Math.abs(dailyReturnPct).toFixed(2)}%
                </div>
              </div>
            </motion.div>

            {/* Total Return Amount */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/5 border border-white/10 p-6 rounded-3xl hover:bg-white/[0.07] bg-gradient-to-br from-purple-500/5 to-transparent transition-all group relative flex-1 flex flex-col justify-center min-h-[120px]"
            >
              <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 blur-3xl -mr-16 -mt-16 pointer-events-none" />
              </div>
              <div className="flex justify-between items-start mb-2">
                <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em]">{lang === 'ko' ? '누적 투자 손익' : 'Total Return Amount'}</p>
                <TooltipHelp text={lang === 'ko' ? "평균 단가 대비 전체 보유 자산의 누적 실적 정보입니다." : "Cumulative performance of all holdings compared to average cost."} />
              </div>
              <div className="flex items-baseline gap-3">
                <h3 className={`text-3xl font-bold tracking-tighter ${totalReturnAmt >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                  {totalReturnAmt >= 0 ? '+' : ''}{formatCurrency(totalReturnAmt, displayMarket)}
                </h3>
                <div className={`text-[11px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1 ${totalReturnPct >= 0 ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                  {totalReturnPct >= 0 ? '▲' : '▼'} {Math.abs(totalReturnPct).toFixed(2)}%
                </div>
              </div>
            </motion.div>
          </div>
        ) : (
          <>
            {/* Daily Return */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/5 border border-white/10 p-6 rounded-3xl hover:bg-white/[0.07] bg-gradient-to-br from-emerald-500/5 to-transparent transition-all group relative"
            >
              <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-3xl -mr-16 -mt-16 pointer-events-none" />
              </div>
              <div className="flex justify-between items-start mb-2">
                <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em]">{lang === 'ko' ? '일간 변동 손익' : 'Daily Return Amount'}</p>
                <TooltipHelp text={lang === 'ko' ? "직전 거래일 종가 대비 현재 자산 가치의 변동 금액과 수익률입니다." : "Change in asset value and return percentage compared to the previous close."} />
              </div>
              <div className="flex items-baseline gap-3">
                <h3 className={`text-3xl font-bold tracking-tighter ${dailyReturnAmt >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {dailyReturnAmt >= 0 ? '+' : ''}{formatCurrency(dailyReturnAmt, displayMarket)}
                </h3>
                <div className={`text-[11px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1 ${dailyReturnPct >= 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                  {dailyReturnPct >= 0 ? '▲' : '▼'} {Math.abs(dailyReturnPct).toFixed(2)}%
                </div>
              </div>
            </motion.div>

            {/* Total Return Amount */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/5 border border-white/10 p-6 rounded-3xl hover:bg-white/[0.07] bg-gradient-to-br from-purple-500/5 to-transparent transition-all group relative"
            >
              <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 blur-3xl -mr-16 -mt-16 pointer-events-none" />
              </div>
              <div className="flex justify-between items-start mb-2">
                <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em]">{lang === 'ko' ? '누적 투자 손익' : 'Total Return Amount'}</p>
                <TooltipHelp text={lang === 'ko' ? "평균 단가 대비 전체 보유 자산의 누적 실적 정보입니다." : "Cumulative performance of all holdings compared to average cost."} />
              </div>
              <div className="flex items-baseline gap-3">
                <h3 className={`text-3xl font-bold tracking-tighter ${totalReturnAmt >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                  {totalReturnAmt >= 0 ? '+' : ''}{formatCurrency(totalReturnAmt, displayMarket)}
                </h3>
                <div className={`text-[11px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1 ${totalReturnPct >= 0 ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                  {totalReturnPct >= 0 ? '▲' : '▼'} {Math.abs(totalReturnPct).toFixed(2)}%
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* Asset Allocation Chart Integrated as 3rd card */}
        {holdings && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col bg-white/5 border border-white/10 rounded-3xl overflow-hidden hover:bg-white/[0.07] bg-gradient-to-br from-indigo-500/5 to-transparent transition-all group relative min-h-[300px] h-full"
          >
            <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-3xl -mr-16 -mt-16 pointer-events-none" />
            </div>
            <AssetAllocationCard holdings={holdings} language={lang} className="border-0 bg-transparent h-full flex-1 p-6" />
          </motion.div>
        )}
      </div>
    </section>
  );
}

const MiniIndicator = ({ label, value, change, suffix = '', isReverse = false, isScore = false, digits }: {
  label: string;
  value?: number;
  change?: number;
  suffix?: string;
  isReverse?: boolean;
  isScore?: boolean;
  digits?: number;
}) => {
  const isPositive = (change || 0) >= 0;
  const colorClass = isReverse
    ? (isPositive ? 'text-red-400' : 'text-emerald-400')
    : (isPositive ? 'text-emerald-400' : 'text-red-400');

  const fractionDigits = digits !== undefined ? digits : (isScore ? 0 : 2);

  return (
    <div className="flex flex-col min-w-0">
      <span className="text-[8px] text-white/30 font-black uppercase tracking-tighter truncate">{label}</span>
      <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5 mt-0.5">
        <span className="text-xs font-mono font-bold text-white whitespace-nowrap">
          {value !== undefined ? (typeof value === 'number' ? value.toLocaleString(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }) : value) : '--'}
          {suffix}
        </span>
        {change !== undefined && (
          <span className={`text-[8px] font-bold whitespace-nowrap ${colorClass}`}>
            {isPositive ? '▲' : '▼'} {Math.abs(change).toFixed(2)}{isScore ? 'p' : '%'}
          </span>
        )}
      </div>
    </div>
  );
};

export function MacroTopStats({
  macro,
  indicators,
  language = 'ko',
  marketMode = 'US',
  isLoading = false,
  onForceRefresh
}: {
  macro: MacroState,
  indicators?: MacroIndicators | null,
  language?: string,
  marketMode?: 'US' | 'KR',
  isLoading?: boolean,
  onForceRefresh?: () => Promise<void>
}) {
  const lang = language || 'ko';
  const [isLiveModalOpen, setIsLiveModalOpen] = useState(false);
  const { thresholds } = useAppConfig();

  const riskHigh = thresholds?.RISK_HIGH_THRESHOLD ?? DASHBOARD_CONFIG.RISK_HIGH_THRESHOLD;
  const riskMedium = thresholds?.RISK_MEDIUM_THRESHOLD ?? DASHBOARD_CONFIG.RISK_MEDIUM_THRESHOLD;
  const weightOverweight = thresholds?.WEIGHT_OVERWEIGHT_THRESHOLD ?? DASHBOARD_CONFIG.WEIGHT_OVERWEIGHT_THRESHOLD;
  const weightNeutral = thresholds?.WEIGHT_NEUTRAL_THRESHOLD ?? DASHBOARD_CONFIG.WEIGHT_NEUTRAL_THRESHOLD;
  const weightUnderweight = thresholds?.WEIGHT_UNDERWEIGHT_THRESHOLD ?? DASHBOARD_CONFIG.WEIGHT_UNDERWEIGHT_THRESHOLD;
  const budgetHigh = thresholds?.BUDGET_HIGH_THRESHOLD ?? DASHBOARD_CONFIG.BUDGET_HIGH_THRESHOLD;
  const budgetMedium = thresholds?.BUDGET_MEDIUM_THRESHOLD ?? DASHBOARD_CONFIG.BUDGET_MEDIUM_THRESHOLD;
  const budgetLow = thresholds?.BUDGET_LOW_THRESHOLD ?? DASHBOARD_CONFIG.BUDGET_LOW_THRESHOLD;
  const trimCritical = thresholds?.TRIM_CRITICAL_THRESHOLD ?? DASHBOARD_CONFIG.TRIM_CRITICAL_THRESHOLD;
  const trimWarning = thresholds?.TRIM_WARNING_THRESHOLD ?? DASHBOARD_CONFIG.TRIM_WARNING_THRESHOLD;

  const riskScore = macro.riskMetrics?.riskScore || 0;
  const macroWeight = macro.riskMetrics?.macroWeight || 0;
  const riskBudget = macro.riskMetrics?.portfolioRiskBudgetPct || 0;
  const trimThreshold = macro.riskMetrics?.trimThresholdPct || 0;

  const riskScoreChange = macro.riskMetrics?.riskScoreChange || 0;
  const macroWeightChange = macro.riskMetrics?.macroWeightChange || 0;
  const riskBudgetChange = macro.riskMetrics?.portfolioRiskBudgetPctChange || 0;
  const trimThresholdChange = macro.riskMetrics?.trimThresholdPctChange || 0;

  const getRiskScoreColor = (score: number) => {
    if (score >= riskHigh) return 'text-red-400';
    if (score >= riskMedium) return 'text-orange-400';
    return 'text-emerald-400';
  };

  const getWeightColor = (weight: number) => {
    if (weight >= weightOverweight) return 'text-emerald-400';
    if (weight >= weightNeutral) return 'text-blue-400';
    if (weight >= weightUnderweight) return 'text-amber-400';
    return 'text-red-400';
  };

  const getBudgetColor = (budget: number) => {
    if (budget >= budgetHigh) return 'text-emerald-400';
    if (budget >= budgetMedium) return 'text-purple-400';
    if (budget >= budgetLow) return 'text-amber-400';
    return 'text-red-400';
  };

  const getTrimColor = (threshold: number) => {
    if (threshold >= trimCritical) return 'text-pink-400';
    if (threshold >= trimWarning) return 'text-amber-400';
    return 'text-red-400';
  };

  const renderChange = (change: number, suffix = '') => {
    if (change === 0) {
      return (
        <span className="text-[10px] font-bold text-white/30 font-mono mt-1 block">
          0.00{suffix}
        </span>
      );
    }
    const isPos = change > 0;
    const color = isPos ? 'text-emerald-400' : 'text-red-400';
    return (
      <span className={`text-[10px] font-bold font-mono mt-1 block ${color}`}>
        {isPos ? '▲' : '▼'} {Math.abs(change).toFixed(2)}{suffix}
      </span>
    );
  };

  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
      {isLoading && (
        <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px] z-50 flex items-center justify-center rounded-2xl border border-white/5">
          <div className="flex flex-col items-center gap-3 bg-black/40 px-6 py-4 rounded-2xl border border-white/10 shadow-2xl backdrop-blur-md">
            <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
            <span className="text-[10px] font-bold text-white/60 tracking-widest uppercase">
              {lang === 'ko' ? '거시 지표 업데이트 중...' : 'Updating Macro...'}
            </span>
          </div>
        </div>
      )}

      {/* 0. Real-time Market Indicators */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => setIsLiveModalOpen(true)}
        className="bg-white/5 border border-white/10 p-6 rounded-2xl hover:bg-white/[0.08] hover:border-indigo-500/40 transition-all bg-gradient-to-br from-indigo-500/5 to-transparent flex flex-col justify-between md:row-span-2 md:col-start-1 md:row-start-1 cursor-pointer group"
      >
        <div className="flex justify-between items-start mb-1">
          <div className="flex items-center gap-2 mb-1">
            <Globe className="w-4 h-4 text-indigo-400 group-hover:scale-110 transition-transform" />
            <p className="text-white/40 group-hover:text-white/70 text-[10px] uppercase font-bold tracking-wider transition-colors">
              {lang === 'ko' ? '시장 지표 (실시간)' : 'Market Indicators (Live)'}
            </p>
          </div>
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 font-bold border border-indigo-500/25 group-hover:bg-indigo-500/25 transition-all">
              {marketMode === 'KR'
                ? `BOK ${indicators?.policyRates?.bok?.display || '3.50%'}`
                : `FOMC ${(indicators?.policyRates?.fed?.targetRateUpper || 0) >= 4.00 ? indicators?.policyRates?.fed?.display : '3.75~4.00%'}`}
            </span>
            <Maximize2 className="w-3.5 h-3.5 text-white/30 group-hover:text-indigo-400 transition-colors" />
            <TooltipHelp text={lang === 'ko' ? "실시간 주요 시장 지표 및 중앙은행 정책금리입니다. 클릭하여 상세 모달을 엽니다." : "Major real-time market indicators and central bank policy rates. Click to open modal."} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2">
          {marketMode === 'KR' ? (
            <>
              <MiniIndicator label="KOSPI" value={indicators?.kospi?.value} change={indicators?.kospi?.change} />
              <MiniIndicator label="KOSDAQ" value={indicators?.kosdaq?.value} change={indicators?.kosdaq?.change} />
              <MiniIndicator label="S&P 500" value={indicators?.sp500?.value} change={indicators?.sp500?.change} />
              <MiniIndicator label="VKOSPI" value={indicators?.vkospi?.value} change={indicators?.vkospi?.change} isReverse />
              <MiniIndicator label="KOSPI FGI" value={indicators?.kospiFgi?.value} change={indicators?.kospiFgi?.change} isScore />
            </>
          ) : (
            <>
              <MiniIndicator label="S&P 500" value={indicators?.sp500?.value} change={indicators?.sp500?.change} />
              <MiniIndicator label="Nasdaq" value={indicators?.nasdaq?.value} change={indicators?.nasdaq?.change} />
              <MiniIndicator label="SOX" value={indicators?.sox?.value} change={indicators?.sox?.change} />
              <MiniIndicator label="Fear & Greed" value={indicators?.fearGreed?.value} change={indicators?.fearGreed?.change} isScore />
              <MiniIndicator label="VIX" value={indicators?.vix?.value} change={indicators?.vix?.change} isReverse />
              <MiniIndicator label="VIXEQ" value={indicators?.vixeq?.value} change={indicators?.vixeq?.change} isReverse />
              <MiniIndicator label="DXY (USD)" value={indicators?.dxy?.value} change={indicators?.dxy?.change} isReverse />
            </>
          )}
        </div>
      </motion.div>

      {/* 1. Macro Risk Score */}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/5 border border-white/10 p-6 rounded-2xl hover:bg-white/[0.07] transition-all md:col-start-2 md:row-start-1"
      >
        <div className="flex justify-between items-start mb-1">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className={`w-4 h-4 ${getRiskScoreColor(riskScore)}`} />
            <p className="text-white/40 text-[10px] uppercase font-bold tracking-wider">{lang === 'ko' ? '거시 리스크 지수' : 'Macro Risk Score'}</p>
          </div>
          <TooltipHelp text={
            marketMode === 'KR'
              ? (lang === 'ko' ? "한국은행 기준금리, KOSPI 변동성, 소비물가(CPI) 등을 종합하여 산출된 리스크 점수입니다." : "Risk score calculated from BOK rate, KOSPI volatility, and CPI.")
              : (lang === 'ko' ? "연준 금리, VIX, 인플레이션 등을 종합하여 산출된 리스크 점수입니다." : "Risk score calculated from Fed rates, VIX, and inflation.")
          } />
        </div>
        <h3 className={`text-3xl font-black ${getRiskScoreColor(riskScore)}`}>{riskScore}</h3>
        {marketMode === 'KR' && renderChange(riskScoreChange)}
      </motion.div>

      {/* 2. Macro Weight */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/5 border border-white/10 p-6 rounded-2xl hover:bg-white/[0.07] transition-all md:col-start-2 md:row-start-2"
      >
        <div className="flex justify-between items-start mb-1">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className={`w-4 h-4 ${getWeightColor(macroWeight)}`} />
            <p className="text-white/40 text-[10px] uppercase font-bold tracking-wider">{lang === 'ko' ? '거시 비중' : 'Macro Weight'}</p>
          </div>
          <TooltipHelp text={lang === 'ko' ? "매크로 리스크를 고려한 포트폴리오 거시 노출 배수입니다." : "Portfolio macro exposure multiplier considering macro risks."} />
        </div>
        <h3 className={`text-3xl font-black ${getWeightColor(macroWeight)}`}>{macroWeight.toFixed(2)}x</h3>
        {marketMode === 'KR' && renderChange(macroWeightChange, 'x')}
      </motion.div>

      {/* 3. Risk Budget */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/5 border border-white/10 p-6 rounded-2xl hover:bg-white/[0.07] transition-all md:col-start-3 md:row-start-1"
      >
        <div className="flex justify-between items-start mb-1">
          <div className="flex items-center gap-2 mb-1">
            <Shield className={`w-4 h-4 ${getBudgetColor(riskBudget)}`} />
            <p className="text-white/40 text-[10px] uppercase font-bold tracking-wider">{lang === 'ko' ? '리스크 예산' : 'Risk Budget'}</p>
          </div>
          <TooltipHelp text={lang === 'ko' ? "현재 거시 환경에서 포트폴리오에 할당된 목표 리스크 예산 비중입니다." : "Target risk budget allocated to the portfolio in the current macro environment."} />
        </div>
        <h3 className={`text-3xl font-black ${getBudgetColor(riskBudget)}`}>{riskBudget.toFixed(1)}%</h3>
        {marketMode === 'KR' && renderChange(riskBudgetChange, '%')}
      </motion.div>

      {/* 4. Trim Threshold */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/5 border border-white/10 p-6 rounded-2xl hover:bg-white/[0.07] transition-all md:col-start-3 md:row-start-2"
      >
        <div className="flex justify-between items-start mb-1">
          <div className="flex items-center gap-2 mb-1">
            <Zap className={`w-4 h-4 ${getTrimColor(trimThreshold)}`} />
            <p className="text-white/40 text-[10px] uppercase font-bold tracking-wider">{lang === 'ko' ? '리밸런싱 임계치' : 'Trim Threshold'}</p>
          </div>
          <TooltipHelp text={
            marketMode === 'KR'
              ? (lang === 'ko' ? "자산 가치가 이 임계치 이상 변동할 경우 한국 시장의 높은 변동성을 반영한 리밸런싱이 권장됩니다." : "Rebalancing is recommended when asset value fluctuates beyond this threshold, reflecting higher KR market volatility.")
              : (lang === 'ko' ? "개별 자산의 수익 또는 손실이 리밸런싱을 촉발하는 임계치 비율입니다." : "Threshold percentage at which individual asset gains or losses trigger rebalancing.")
          } />
        </div>
        <h3 className={`text-3xl font-black ${getTrimColor(trimThreshold)}`}>{trimThreshold.toFixed(1)}%</h3>
        {marketMode === 'KR' && renderChange(trimThresholdChange, '%')}
      </motion.div>

      <LiveMarketIndicatorsModal
        isOpen={isLiveModalOpen}
        onClose={() => setIsLiveModalOpen(false)}
        indicators={indicators}
        language={lang}
        marketMode={marketMode}
        onForceRefresh={onForceRefresh}
      />
    </section>
  );
}

// 2. AI Analysis Card (Professional Analyst Report Style)
export function AiAnalysisCard({
  macroSummary,
  aiAdvice,
  isLoading,
  showSummary = true,
  showAdvice = true,
  title,
  isFullHeight = false,
  language = 'ko',
  onRunAnalysis,
  onStopAnalysis,
  analysisProgress,
  errorInfo,
  generationDate
}: {
  macroSummary?: string,
  aiAdvice?: string,
  isLoading: boolean,
  showSummary?: boolean,
  showAdvice?: boolean,
  title?: string,
  isFullHeight?: boolean,
  language?: string,
  onRunAnalysis?: () => void,
  onStopAnalysis?: () => void,
  analysisProgress?: number,
  errorInfo?: AIErrorInfo | null,
  generationDate?: string
}) {
  const lang = language || 'ko';
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`bg-[#0a0a0a] border border-white/10 rounded-3xl relative overflow-hidden flex flex-col h-full ${isFullHeight ? 'min-h-[800px]' : ''}`}
    >
      {/* Report Header */}
      <div className="border-b border-white/10 p-6 bg-gradient-to-r from-emerald-900/20 to-transparent flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
            <Sparkles className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">AI Strategic Desk</span>
            </div>
            <h2 className="text-xl font-bold text-white">{title || (lang === 'ko' ? 'AI 포트폴리오 최적화 리포트' : 'AI Portfolio Optimization Report')}</h2>
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <div className="flex flex-col items-end gap-2">
            <div className="flex flex-col">
              <div className="text-xs text-white/40 uppercase font-bold tracking-wider mb-1">{lang === 'ko' ? '생성일시' : 'Generated At'}</div>
              <div className="text-sm font-mono text-emerald-400 font-bold">
                {isLoading ? (lang === 'ko' ? '분석 중...' : 'Analyzing...') : (generationDate ? new Date(generationDate).toLocaleString() : new Date().toLocaleString())}
              </div>
            </div>
            {aiAdvice && !isLoading && onRunAnalysis && (
              <button
                onClick={onRunAnalysis}
                className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-emerald-500/20 transition-all font-mono"
              >
                <RefreshCw className="w-3 h-3" />
                {lang === 'ko' ? '다시 분석' : 'RE-ANALYZE'}
              </button>
            )}
            {isLoading && onStopAnalysis && (
              <button
                onClick={onStopAnalysis}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-red-500/20 transition-all font-mono"
              >
                {lang === 'ko' ? '중단' : 'STOP'}
              </button>
            )}
            {!isLoading && checkIfAdmin() && (
              <button
                onClick={async () => {
                  if (!await window.confirm(`${lang === 'ko' ? '포트폴리오 분석 캐시를 강제 갱신하시겠습니까?' : 'Force refresh Portfolio analysis cache?'}`)) return;
                  try {
                    const token = await auth.currentUser?.getIdToken();
                    const adminToken = sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token');
                    const headers: Record<string, string> = {
                      'Content-Type': 'application/json'
                    };
                    if (token) headers['Authorization'] = `Bearer ${token}`;
                    if (adminToken) headers['X-Admin-Authorization'] = `Bearer ${adminToken}`;

                    const res = await fetch('/api/admin/force-refresh', {
                      method: 'POST',
                      headers,
                      credentials: 'include',
                      body: JSON.stringify({ categoryId: 'macro' })
                    });
                    if (!res.ok) {
                      const errData = (await res.json().catch(() => ({}))) as { error?: string };
                      throw new Error(errData.error || `HTTP ${res.status}`);
                    }
                    if (onRunAnalysis) onRunAnalysis();
                  } catch (e) {
                    console.error("Force refresh failed", e);
                  }
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-purple-500/20 transition-all font-mono mt-1"
                title="Force UI Refresh (Admin)"
              >
                <RefreshCcw className="w-3 h-3" />
                {lang === 'ko' ? '캐시 갱신' : 'REFRESH V-CACHE'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar for AI Engine */}
      {isLoading && analysisProgress !== undefined && (
        <div className="relative">
          <div className="h-1 w-full bg-white/5 relative">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${analysisProgress}%` }}
              className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]"
            />
          </div>
          <div className="absolute right-4 -top-6">
            <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
              {Math.round(analysisProgress)}%
            </span>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {errorInfo && !isLoading && (
        <div className={`mx-8 mt-6 p-5 border rounded-2xl relative overflow-hidden ${
          errorInfo.code === 'TIER_LIMIT_EXCEEDED'
            ? 'bg-gradient-to-r from-amber-500/10 via-amber-600/5 to-purple-500/10 border-amber-500/30'
            : 'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="absolute top-0 right-0 p-3 opacity-20">
            {errorInfo.code === 'TIER_LIMIT_EXCEEDED' ? (
              <Crown className="w-10 h-10 text-amber-400" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-red-400" />
            )}
          </div>
          <div className="flex items-start gap-3 relative z-10">
            <div className={`p-2.5 rounded-xl shrink-0 ${
              errorInfo.code === 'TIER_LIMIT_EXCEEDED'
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                : 'bg-red-500/20 text-red-400'
            }`}>
              {errorInfo.code === 'TIER_LIMIT_EXCEEDED' ? (
                <Lock className="w-5 h-5 text-amber-400" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className={`text-sm font-bold ${errorInfo.code === 'TIER_LIMIT_EXCEEDED' ? 'text-amber-300' : 'text-red-400'}`}>
                  {errorInfo.title || (lang === 'ko' ? 'AI 분석 오류' : 'AI Analysis Error')}
                </h4>
                {errorInfo.code === 'TIER_LIMIT_EXCEEDED' && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 uppercase tracking-wider">
                    Limit Reached
                  </span>
                )}
              </div>
              <p className={`text-xs leading-relaxed mb-2 ${errorInfo.code === 'TIER_LIMIT_EXCEEDED' ? 'text-amber-200/90' : 'text-red-300/80'}`}>
                {errorInfo.message}
              </p>
              {errorInfo.suggestion && (
                <p className={`text-xs leading-relaxed ${errorInfo.code === 'TIER_LIMIT_EXCEEDED' ? 'text-amber-300/70' : 'text-red-300/60'}`}>
                  💡 {errorInfo.suggestion}
                </p>
              )}

              {errorInfo.code === 'TIER_LIMIT_EXCEEDED' && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('open:upgrade-modal', {
                        detail: {
                          requiredTier: errorInfo.requiredTier || 'TIER3',
                          featureName: lang === 'ko' ? 'AI 포트폴리오 진단' : 'AI Portfolio Intelligence'
                        }
                      }));
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black text-xs font-bold rounded-xl shadow-lg shadow-amber-500/20 transition-all font-mono cursor-pointer"
                  >
                    <Zap className="w-3.5 h-3.5 fill-black" />
                    {lang === 'ko'
                      ? `${errorInfo.requiredTier || 'TIER3'} 등급으로 업그레이드하기`
                      : `Upgrade to ${errorInfo.requiredTier || 'TIER3'}`}
                    <ArrowRight className="w-3.5 h-3.5 ml-0.5" />
                  </button>
                  <button
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('navigate:settings'));
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/80 text-xs font-medium rounded-xl border border-white/10 transition-all cursor-pointer"
                  >
                    {lang === 'ko' ? '멤버십 혜택 비교' : 'Compare Tier Plans'}
                  </button>
                </div>
              )}

              {errorInfo.code === 'RATE_LIMIT' && (
                <button
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('navigate:settings'));
                  }}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-amber-500/20 transition-all"
                >
                  <Settings className="w-3 h-3" />
                  {lang === 'ko' ? '설정 > API 키 설정' : 'Settings > API Key Setup'}
                </button>
              )}

              {onRunAnalysis && errorInfo.code !== 'TIER_LIMIT_EXCEEDED' && (
                <button
                  onClick={onRunAnalysis}
                  className="mt-3 ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-emerald-500/20 transition-all"
                >
                  <RefreshCw className="w-3 h-3" />
                  {lang === 'ko' ? '재시도' : 'Retry'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Report Body */}
      <div className="p-8 space-y-8 flex-1 overflow-y-auto custom-scrollbar min-h-[400px]">

        {showSummary && (
          <section className="bg-blue-500/5 border border-blue-500/10 rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Zap className="w-12 h-12 text-blue-400" />
            </div>
            <h3 className="flex items-center gap-2 text-sm font-bold text-blue-400 uppercase tracking-widest mb-4">
              <Zap className="w-4 h-4" />
              {lang === 'ko' ? '시장 퀵스캔 (Market Quick Scan)' : 'Market Quick Scan'}
            </h3>
            <div className="text-white/80 leading-relaxed text-sm markdown-body">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
                  <div className="text-white/40 italic font-mono uppercase tracking-widest text-xs">
                    {lang === 'ko' ? 'AI 전략 엔진 가동 중...' : 'ENGAGING AI STRATEGY ENGINE...'}
                  </div>
                </div>
              ) : (
                macroSummary ? (
                  <Markdown remarkPlugins={[remarkGfm]}>{macroSummary}</Markdown>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 bg-white/[0.02] rounded-2xl border border-white/5 border-dashed">
                    <p className="text-white/40 italic text-center text-sm">
                      {lang === 'ko' ? "거시 요약 정보가 없습니다." : "No macro summary available."}
                    </p>
                  </div>
                )
              )}
            </div>
          </section>
        )}

        {showSummary && showAdvice && (
          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        )}

        {/* Actionable Advice */}
        {showAdvice && (
          <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
            <h3 className="flex items-center gap-2 text-sm font-bold text-emerald-400 uppercase tracking-wider mb-4">
              <Target className="w-4 h-4" />
              {lang === 'ko' ? '포트폴리오 최적화 전략' : 'Portfolio Optimization Strategy'}
            </h3>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-24 gap-6">
                <ScannerPulse />
                <div className="flex flex-col items-center gap-2">
                  <div className="text-white/40 italic font-mono uppercase tracking-widest text-[10px] font-black">
                    {lang === 'ko' ? 'AI 전략 최적화 엔진 가동 중' : 'OPTIMIZING STRATEGY ENGINE'}
                  </div>
                  <div className="text-emerald-400 font-mono text-sm font-black">
                    {Math.round(analysisProgress)}%
                  </div>
                </div>
                {onStopAnalysis && (
                  <button
                    onClick={onStopAnalysis}
                    className="mt-4 px-4 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-red-500/20 transition-all font-mono"
                  >
                    {lang === 'ko' ? '분석 중단' : 'STOP ANALYSIS'}
                  </button>
                )}
              </div>
            ) : (
              <div className="text-white/80 leading-relaxed text-sm markdown-body">
                {aiAdvice ? (
                  <Markdown remarkPlugins={[remarkGfm]}>{aiAdvice}</Markdown>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 space-y-4">
                    <p className="text-white/40 italic text-center">
                      {lang === 'ko'
                        ? "매크로 환경과 포트폴리오 정보를 조합한 정밀 분석이 필요합니다."
                        : "Deep analysis combining macro environment and portfolio data is required."}
                    </p>
                    {onRunAnalysis && (
                      <button
                        onClick={onRunAnalysis}
                        className="flex items-center gap-3 px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] group"
                      >
                        <PieChart className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                        {lang === 'ko' ? 'AI 포트폴리오 전략 생성' : 'Generate AI Portfolio Strategy'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </motion.div>
  );
}
// Scanner Animation Component
function ScannerPulse() {
  return (
    <div className="relative w-24 h-24 mb-6 flex items-center justify-center">
      <motion.div
        animate={{ scale: [1, 2], opacity: [0.5, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
        className="absolute inset-0 bg-blue-500/20 rounded-full border border-blue-500/50"
      />
      <motion.div
        animate={{ scale: [1, 2.5], opacity: [0.3, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.6 }}
        className="absolute inset-0 bg-blue-400/10 rounded-full border border-blue-400/30"
      />
      <motion.div
        animate={{ scale: [1, 3], opacity: [0.1, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 1.2 }}
        className="absolute inset-0 bg-blue-300/5 rounded-full border border-blue-300/20"
      />
      <div className="relative z-10 p-4 bg-blue-500/10 rounded-full border border-blue-500/30 shadow-[0_0_30px_rgba(59,130,246,0.3)]">
        <Radar className="w-8 h-8 text-blue-400" />
      </div>
    </div>
  );
}

// 2. Macro Analysis View (v1.8.6)
export function MacroAnalysisView({
  executiveSummary,
  deepAnalysis,
  isLoading,
  isFullHeight = false,
  language = 'ko',
  analysisProgress = 0,
  analysisStage = 0,
  asOfDate,
  onFetchIntelligence,
  onStopAnalysis,
  isQuickScanLoading = false,
  reportFormat = 'text',
  onSetReportFormat,
  macro,
  indicators,
  marketMode,
  onGenerateQuickSummary
}: {
  executiveSummary: string,
  deepAnalysis: string,
  isLoading: boolean,
  isFullHeight?: boolean,
  language?: string,
  analysisProgress?: number,
  analysisStage?: 0 | 1 | 2 | 3,
  asOfDate?: string,
  onFetchIntelligence?: (force?: boolean) => void,
  onStopAnalysis?: () => void,
  isQuickScanLoading?: boolean,
  reportFormat?: 'text' | 'compact' | 'visual' | 'technical',
  onSetReportFormat?: (format: 'text' | 'compact' | 'visual' | 'technical') => void,
  macro: MacroState,
  indicators?: MacroIndicators | null,
  marketMode?: 'US' | 'KR',
  onGenerateQuickSummary?: (force?: boolean) => void
}) {
  const lang = language || 'ko';
  const currentFormat = reportFormat;
  const [isDetailedVisible, setIsDetailedVisible] = useState(!!deepAnalysis);
  const [isQuickScanExpanded, setIsQuickScanExpanded] = useState(true);
  const [macroSubTab, setMacroSubTab] = useState<'overview' | 'deep' | 'regime'>('overview');

  // Sync state when deepAnalysis changes
  React.useEffect(() => {
    setIsDetailedVisible(!!deepAnalysis);
  }, [deepAnalysis]);

  // If newly loading, ensure visibility logic tracks it
  React.useEffect(() => {
    if (isLoading) {
      setIsDetailedVisible(true);
    }
  }, [isLoading]);

  // Extract Market Mood and Clean Summary
  const { mood, cleanSummary } = React.useMemo(() => {
    if (!executiveSummary) return { mood: null, cleanSummary: null };

    let currentMood = null;
    const moodMatch = executiveSummary.match(/\[MARKET_MOOD\]:\s*(.+)/i);
    if (moodMatch) currentMood = moodMatch[1].split('\n')[0].trim();

    const clean = executiveSummary
      .replace(/Market Quick Scan\s*\|[^\n]*\n+/gi, '')
      .replace(/기준 시각:[^\n]*\n+/gi, '')
      .replace(/\[MARKET_MOOD\]:.+/, '')
      .replace(/\[KEY_STRATEGIES\]/gi, '\n\n### 🎯 핵심 투자 전략 (Key Strategies)')
      .replace(/\[INVESTMENT_STRATEGY\](?!\s*$)/g, '\n\n### 💡 전략적 함의 (Investment Strategy)')
      .replace(/> \[!(?:IMPORTANT|NOTE|TIP|WARNING|CAUTION)\][\s\S]*?(?=\n\n|\n##|$)/gi, '')
      .replace(/각 항목은 \*\*주어-술어 구조의 명확한 요약문\*\*으로 시작하십시오\./g, '')
      .replace(/항목당 최대 2문장 이내로 아주 간결하게 작성하십시오\./g, '')
      .trim();

    return { mood: currentMood, cleanSummary: clean };
  }, [executiveSummary]);

  // Clean Deep Analysis and strip markers
  const cleanDeepAnalysis = React.useMemo(() => {
    if (!deepAnalysis) return null;

    return deepAnalysis
      .replace(/\[REGIME_ANALYSIS\]/g, '')
      .replace(/\[NEWS_DATA_ANALYSIS\]/g, '')
      .replace(/\[INVESTMENT_STRATEGY\]/g, '')
      .replace(/\[FINAL_ORDER\]/g, '')
      .replace(/\[EOF\]/g, '')
      .replace(/> \[!(?:IMPORTANT|NOTE|TIP|WARNING|CAUTION)\][\s\S]*?(?=\n\n|\n##|$)/gi, '')
      .replace(/신호 분석 섹션으로 바로 시작하십시오\.?/g, '')
      .replace(/I will now start the report\.?/g, '')
      .replace(/각 항목은 \*\*주어-술어 구조의 명확한 요약문\*\*으로 시작하십시오\./g, '')
      .replace(/항목당 최대 2문장 이내로 아주 간결하게 작성하십시오\./g, '')
  }, [deepAnalysis]);

  // Extract compact bullet points
  const compactSummaryBullets = React.useMemo(() => {
    if (!cleanSummary) return [];
    return cleanSummary
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.startsWith('- ') || line.startsWith('* ') || /^\d+\.\s+/.test(line))
      .map(line => line.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '').trim())
      .filter(line => line.length > 5 && !line.startsWith('#'))
      .slice(0, 6);
  }, [cleanSummary]);

  const compactDeepBullets = React.useMemo(() => {
    if (!cleanDeepAnalysis) return [];
    return cleanDeepAnalysis
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.startsWith('- ') || line.startsWith('* ') || /^\d+\.\s+/.test(line))
      .map(line => line.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '').trim())
      .filter(line => line.length > 5 && !line.startsWith('#'))
      .slice(0, 8);
  }, [cleanDeepAnalysis]);

  // Quantitative Technical Macro Indicators (Live SSOT)
  const technicalIndicators = React.useMemo(() => {
    const isKr = marketMode === 'KR';
    return [
      {
        id: 'rate',
        name: isKr ? (lang === 'ko' ? '한국 기준금리' : 'BOK Base Rate') : (lang === 'ko' ? '미국 기준금리 (Fed Funds)' : 'Fed Funds Rate'),
        value: indicators?.fedFunds?.value != null ? `${indicators.fedFunds.value.toFixed(2)}%` : (macro?.fedFunds?.value != null ? `${macro.fedFunds.value.toFixed(2)}%` : '-'),
        change: indicators?.fedFunds?.change,
        unit: '%',
        source: isKr ? '한국은행 (ECOS)' : 'Federal Reserve (FRED)',
        date: indicators?.fedFunds?.date || macro?.fedFunds?.date || macro?.asOfDate || '-',
        stance: macro?.fedFunds?.value != null ? (macro.fedFunds.value >= (isKr ? 3.5 : 5.0) ? (lang === 'ko' ? '긴축 기조' : 'Restrictive') : (lang === 'ko' ? '중립/완화' : 'Neutral/Easing')) : (lang === 'ko' ? '운용 중' : 'Standard')
      },
      {
        id: 'cpi',
        name: isKr ? (lang === 'ko' ? '소비자물가지수 (CPI)' : 'Korea CPI') : (lang === 'ko' ? '헤드라인 CPI' : 'US CPI Inflation'),
        value: indicators?.inflation?.value != null ? `${indicators.inflation.value.toFixed(1)}%` : (macro?.inflation?.value != null ? `${macro.inflation.value.toFixed(1)}%` : '-'),
        change: indicators?.inflation?.change,
        unit: '%',
        source: isKr ? '통계청 (KOSIS)' : 'U.S. BLS',
        date: indicators?.inflation?.date || macro?.inflation?.date || '-',
        stance: (macro?.inflation?.value ?? indicators?.inflation?.value ?? 0) > 3.0 ? (lang === 'ko' ? '물가 압력 잔존' : 'Sticky Inflation') : (lang === 'ko' ? '목표치 수렴' : 'Near Target')
      },
      {
        id: 'unemp',
        name: lang === 'ko' ? '실업률 (Unemployment)' : 'Unemployment Rate',
        value: indicators?.unemployment?.value != null ? `${indicators.unemployment.value.toFixed(1)}%` : (macro?.unemployment?.value != null ? `${macro.unemployment.value.toFixed(1)}%` : '-'),
        change: indicators?.unemployment?.change,
        unit: '%',
        source: isKr ? '통계청' : 'U.S. BLS',
        date: indicators?.unemployment?.date || macro?.unemployment?.date || '-',
        stance: (macro?.unemployment?.value ?? indicators?.unemployment?.value ?? 0) > 4.3 ? (lang === 'ko' ? '고용 둔화세' : 'Softening') : (lang === 'ko' ? '고용 견고' : 'Solid Labor')
      },
      {
        id: '10y',
        name: isKr ? (lang === 'ko' ? '국고채 10년물 금리' : 'KR 10Y Treasury') : (lang === 'ko' ? '미국채 10년물 금리' : 'US 10Y Treasury'),
        value: (isKr && indicators?.government?.krTreasury10Y?.value != null) ? `${indicators.government.krTreasury10Y.value.toFixed(2)}%` : (indicators?.government?.treasury10Y?.value != null ? `${indicators.government.treasury10Y.value.toFixed(2)}%` : (macro?.government?.treasury10Y?.value != null ? `${macro.government.treasury10Y.value.toFixed(2)}%` : '-')),
        change: (isKr && indicators?.government?.krTreasury10Y?.change != null) ? indicators.government.krTreasury10Y.change : (indicators?.government?.treasury10Y?.change ?? macro?.government?.treasury10Y?.change),
        unit: '%',
        source: isKr ? '금융투자협회' : 'U.S. Treasury',
        date: indicators?.government?.treasury10Y?.date || macro?.government?.treasury10Y?.date || '-',
        stance: lang === 'ko' ? '벤치마크 금리' : 'Benchmark'
      },
      {
        id: 'spread',
        name: lang === 'ko' ? '10Y-2Y 장단기 금리차' : '10Y-2Y Yield Spread',
        value: indicators?.yieldSpread?.value != null ? `${indicators.yieldSpread.value >= 0 ? '+' : ''}${indicators.yieldSpread.value.toFixed(2)}%p` : (macro?.government?.yieldSpread10Y3M?.value != null ? `${macro.government.yieldSpread10Y3M.value >= 0 ? '+' : ''}${macro.government.yieldSpread10Y3M.value.toFixed(2)}%p` : '-'),
        change: indicators?.yieldSpread?.change ?? macro?.government?.yieldSpread10Y3M?.change,
        unit: '%p',
        source: 'FRED (T10Y2Y)',
        date: indicators?.yieldSpread?.date || macro?.government?.yieldSpread10Y3M?.date || '-',
        stance: (indicators?.yieldSpread?.value ?? macro?.government?.yieldSpread10Y3M?.value ?? 0) < 0 ? (lang === 'ko' ? '수익률 역전 (침체 경계)' : 'Inverted') : (lang === 'ko' ? '정상화 진행' : 'Normal')
      },
      {
        id: 'vix',
        name: isKr ? (lang === 'ko' ? 'VKOSPI (국내 변동성)' : 'VKOSPI') : (lang === 'ko' ? 'VIX (시장 공포지수)' : 'CBOE VIX'),
        value: (isKr && indicators?.vkospi?.value != null) ? `${indicators.vkospi.value.toFixed(2)}` : (indicators?.vix?.value != null ? `${indicators.vix.value.toFixed(2)}` : (macro?.government?.vkospi?.value != null ? `${macro.government.vkospi.value.toFixed(2)}` : '-')),
        change: (isKr && indicators?.vkospi?.change != null) ? indicators.vkospi.change : (indicators?.vix?.change ?? macro?.government?.vkospi?.change),
        unit: 'pts',
        source: isKr ? 'KRX 한국거래소' : 'CBOE',
        date: indicators?.vix?.date || macro?.government?.vkospi?.date || '-',
        stance: ((isKr ? indicators?.vkospi?.value : (indicators?.vix?.value ?? macro?.government?.vkospi?.value)) ?? 0) > 20 ? (lang === 'ko' ? '위험 회피 (고변동성)' : 'Risk-Off') : (lang === 'ko' ? '위험 선호 (안정)' : 'Risk-On')
      },
      {
        id: 'fx',
        name: isKr ? (lang === 'ko' ? '원/달러 환율 (USD/KRW)' : 'USD/KRW FX') : (lang === 'ko' ? '달러 인덱스 (DXY)' : 'US Dollar Index (DXY)'),
        value: isKr ? (indicators?.usdkrw?.value != null ? `₩${indicators.usdkrw.value.toLocaleString()}` : '-') : (indicators?.dxy?.value != null ? `${indicators.dxy.value.toFixed(2)}` : '-'),
        change: isKr ? indicators?.usdkrw?.change : indicators?.dxy?.change,
        unit: isKr ? '원' : 'pts',
        source: isKr ? '서울외환시장' : 'ICE Benchmark',
        date: isKr ? (indicators?.usdkrw?.date || '-') : (indicators?.dxy?.date || '-'),
        stance: isKr ? ((indicators?.usdkrw?.value ?? 0) >= 1350 ? (lang === 'ko' ? '고환율 경계' : 'High FX Alert') : (lang === 'ko' ? '환율 안정' : 'Stable FX')) : ((indicators?.dxy?.value ?? 0) >= 104 ? (lang === 'ko' ? '강달러 기조' : 'Strong USD') : (lang === 'ko' ? '달러 안정세' : 'Neutral USD'))
      },
      {
        id: 'm2',
        name: lang === 'ko' ? '광의 통화량 (M2 Supply)' : 'M2 Money Supply',
        value: indicators?.m2MoneySupply?.value != null ? `${(indicators.m2MoneySupply.value / 1000).toFixed(2)}T` : (indicators?.m2?.value != null ? `${indicators.m2.value.toFixed(1)}B` : '-'),
        change: indicators?.m2MoneySupply?.change ?? indicators?.m2?.change,
        unit: 'T',
        source: 'Federal Reserve (FRED)',
        date: indicators?.m2MoneySupply?.date || indicators?.m2?.date || '-',
        stance: lang === 'ko' ? '유동성 공급' : 'Global Liquidity'
      }
    ];
  }, [indicators, macro, marketMode, lang]);

  // Render report content formatted according to selected format
  const renderFormattedSection = (
    markdownText: string | null,
    bullets: string[],
    isDeep: boolean
  ) => {
    if (!markdownText) return null;

    if (currentFormat === 'compact') {
      return (
        <div className="space-y-6">
          <div className="p-5 rounded-2xl bg-gradient-to-br from-blue-500/10 via-white/[0.02] to-indigo-500/10 border border-blue-500/20 space-y-4 shadow-lg">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-xs font-black text-blue-400 uppercase tracking-widest">
                <LayoutDashboard className="w-4 h-4" />
                {lang === 'ko' ? '핵심 브리핑 요약 (TL;DR)' : 'Executive Briefing (TL;DR)'}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[10px] font-extrabold uppercase">
                {lang === 'ko' ? '요약 모드' : 'Compact Mode'}
              </span>
            </div>

            {bullets.length > 0 ? (
              <div className="space-y-2.5">
                {bullets.map((b, bIdx) => (
                  <div key={bIdx} className="flex items-start gap-2.5 p-3 rounded-xl bg-black/40 border border-white/5">
                    <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                    <span className="text-xs text-white/90 leading-relaxed font-medium">{b}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="text-white/80 leading-relaxed text-[13px] markdown-body line-clamp-6 hover:line-clamp-none transition-all">
            <Markdown remarkPlugins={[remarkGfm]}>{markdownText}</Markdown>
          </div>
        </div>
      );
    }

    if (currentFormat === 'visual') {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">{lang === 'ko' ? '거시 리스크' : 'Macro Risk'}</span>
                <Activity className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-xl font-black ${macro.riskMetrics?.riskScore >= 60 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {macro.riskMetrics?.riskScore || 0}
                </span>
                <span className="text-[10px] text-white/30 font-bold">/ 100</span>
              </div>
              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${macro.riskMetrics?.riskScore >= 60 ? 'bg-red-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, macro.riskMetrics?.riskScore || 0)}%` }}
                />
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">{lang === 'ko' ? '시장 국면' : 'Regime'}</span>
                <Shield className="w-3.5 h-3.5 text-purple-400" />
              </div>
              <div>
                <RegimeBadge regime={macro.marketRegime} lang={lang as 'ko' | 'en'} />
              </div>
              <p className="text-[10px] text-white/40 truncate">{lang === 'ko' ? 'AI 체제 분석 가동 중' : 'Active AI Engine'}</p>
            </div>

            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">{lang === 'ko' ? '기준 금리' : 'Policy Rate'}</span>
                <BarChart3 className="w-3.5 h-3.5 text-indigo-400" />
              </div>
              <div className="text-xl font-black text-white font-mono">
                {indicators?.fedFunds?.value != null ? `${indicators.fedFunds.value.toFixed(2)}%` : (macro?.fedFunds?.value != null ? `${macro.fedFunds.value.toFixed(2)}%` : '-')}
              </div>
              <p className="text-[10px] text-indigo-300/80 font-medium">
                {macro?.fedFunds?.value != null ? (macro.fedFunds.value >= 5 ? (lang === 'ko' ? '고금리 기조' : 'Restrictive') : (lang === 'ko' ? '중립 완화' : 'Neutral')) : '-'}
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">{lang === 'ko' ? '시장 변동성' : 'VIX'}</span>
                <Zap className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <div className="text-xl font-black text-white font-mono">
                {indicators?.vix?.value != null ? indicators.vix.value.toFixed(1) : (macro?.government?.vkospi?.value != null ? macro.government.vkospi.value.toFixed(1) : '-')}
              </div>
              <p className={`text-[10px] font-medium ${(indicators?.vix?.value ?? 0) > 20 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {(indicators?.vix?.value ?? 0) > 20 ? (lang === 'ko' ? '변동성 확대' : 'Elevated Vol') : (lang === 'ko' ? '시장 안정' : 'Calm Market')}
              </p>
            </div>
          </div>

          <div className="text-white/80 leading-relaxed text-[14px] markdown-body">
            <Markdown remarkPlugins={[remarkGfm]}>{markdownText}</Markdown>
          </div>
        </div>
      );
    }

    if (currentFormat === 'technical') {
      return (
        <div className="space-y-6">
          <div className="rounded-2xl border border-white/10 overflow-hidden bg-black/40 shadow-xl">
            <div className="p-3.5 bg-white/5 border-b border-white/10 flex items-center justify-between">
              <span className="flex items-center gap-2 text-xs font-black text-emerald-400 uppercase tracking-widest font-mono">
                <Database className="w-3.5 h-3.5" />
                {lang === 'ko' ? '실시간 거시경제 정량 지표 데이터 테이블' : 'Quantitative Macro Indicators Table'}
              </span>
              <span className="text-[10px] text-white/40 font-mono">SSOT Live Feed</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/5 bg-white/[0.02] text-white/40 text-[10px] font-bold uppercase tracking-wider">
                    <th className="py-2.5 px-4">{lang === 'ko' ? '지표명' : 'Indicator'}</th>
                    <th className="py-2.5 px-3 text-right">{lang === 'ko' ? '현재값' : 'Current'}</th>
                    <th className="py-2.5 px-3 text-right">{lang === 'ko' ? '변동' : 'Change'}</th>
                    <th className="py-2.5 px-3">{lang === 'ko' ? '상태 / 신호' : 'Stance / Signal'}</th>
                    <th className="py-2.5 px-3 hidden sm:table-cell">{lang === 'ko' ? '출처' : 'Source'}</th>
                    <th className="py-2.5 px-3 hidden md:table-cell">{lang === 'ko' ? '기준일' : 'As of'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-white/80 font-mono">
                  {technicalIndicators.map((item) => (
                    <tr key={item.id} className="hover:bg-white/[0.03] transition-colors">
                      <td className="py-2.5 px-4 font-sans font-bold text-white flex items-center gap-2">
                        {item.name}
                      </td>
                      <td className="py-2.5 px-3 text-right font-black text-white">
                        {item.value}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {item.change != null ? (
                          <span className={`text-[11px] font-bold ${item.change > 0 ? 'text-emerald-400' : item.change < 0 ? 'text-red-400' : 'text-white/40'}`}>
                            {item.change > 0 ? '+' : ''}{item.change.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-white/20">-</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 font-sans">
                        <span className="px-2 py-0.5 rounded-md bg-white/5 text-[10px] font-bold text-white/70 border border-white/5">
                          {item.stance}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 hidden sm:table-cell text-[10px] text-white/40 font-sans">
                        {item.source}
                      </td>
                      <td className="py-2.5 px-3 hidden md:table-cell text-[10px] text-white/30">
                        {item.date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-white/80 leading-relaxed text-[14px] markdown-body">
            <Markdown remarkPlugins={[remarkGfm]}>{markdownText}</Markdown>
          </div>
        </div>
      );
    }

    // Default: 'text'
    return (
      <div className="text-white/90 leading-relaxed text-[14px] markdown-body">
        <Markdown remarkPlugins={[remarkGfm]}>{markdownText}</Markdown>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <MacroIndicatorSummaryCard
        macro={macro}
        indicators={indicators}
        language={lang}
        marketMode={marketMode}
      />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden flex flex-col h-full ${
            macroSubTab === 'overview'
              ? (isFullHeight ? 'min-h-[600px]' : 'min-h-[500px]')
              : (isFullHeight ? 'min-h-[1200px]' : 'min-h-[1000px] max-h-[1600px]')
          }`}
        >
          <div className="p-4 md:p-6 border-b border-white/10 bg-gradient-to-r from-blue-900/20 to-transparent flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 rounded-xl border border-blue-500/20">
                <Activity className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white uppercase italic tracking-tighter flex flex-wrap items-center gap-2">
                  {lang === 'ko' ? '거시 전략 인텔리전스' : 'Macro Strategic Intelligence'}
                  {marketMode && (
                    <span className={`text-[10px] font-mono not-italic uppercase tracking-normal font-bold px-2 py-0.5 rounded-full border shrink-0 ${marketMode === 'KR'
                      ? 'bg-red-500/10 text-red-400 border-red-500/20'
                      : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                      }`}>
                      {marketMode === 'KR' ? (lang === 'ko' ? '🇰🇷 한국 증시 모드' : '🇰🇷 KR Market') : (lang === 'ko' ? '🇺🇸 미국 증시 모드' : '🇺🇸 US Market')}
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-2">
                  <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold font-mono">
                    {lang === 'ko' ? 'REAL-TIME MACRO ANALYTICS' : 'REAL-TIME MACRO ANALYTICS'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {!isLoading && !isQuickScanLoading && (
                <>
                  {/* Report Format Selector */}
                  <div className="flex items-center bg-white/5 rounded-xl p-1 border border-white/10 gap-1 mr-2">
                    {[
                      { id: 'text', icon: LayoutList, label: lang === 'ko' ? '기본' : 'Text' },
                      { id: 'compact', icon: LayoutDashboard, label: lang === 'ko' ? '요약' : 'Compact' },
                      { id: 'visual', icon: BarChart3, label: lang === 'ko' ? '시각' : 'Visual' },
                      { id: 'technical', icon: Database, label: lang === 'ko' ? '기술' : 'Data' }
                    ].map((f) => (
                      <button
                        key={f.id}
                        onClick={() => onSetReportFormat?.(f.id as any)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-[10px] font-bold uppercase tracking-wider cursor-pointer ${currentFormat === f.id
                          ? 'bg-blue-500 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]'
                          : 'text-white/40 hover:text-white/60 hover:bg-white/5'
                          }`}
                        title={f.label}
                      >
                        <f.icon className="w-3 h-3" />
                        <span className="hidden sm:inline">{f.label}</span>
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => {
                      if (macroSubTab === 'deep') {
                        onFetchIntelligence?.(true);
                      } else {
                        onGenerateQuickSummary?.(true);
                      }
                    }}
                    disabled={isLoading || isQuickScanLoading}
                    className="hidden lg:flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 text-[10px] font-bold rounded-lg border border-blue-500/20 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed mr-1"
                    title={lang === 'ko' ? '현재 포맷에 맞춰 AI 분석 재실행' : 'Re-run AI analysis with current format'}
                  >
                    <RefreshCw className={`w-3 h-3 ${isLoading || isQuickScanLoading ? 'animate-spin' : ''}`} />
                    <span>{lang === 'ko' ? '포맷 AI 분석' : 'Format AI'}</span>
                  </button>

                  {checkIfAdmin() && (
                    <button
                      onClick={async () => {
                        if (!await window.confirm(`${lang === 'ko' ? '매크로 인텔리전스 캐시를 강제 갱신하시겠습니까?' : 'Force refresh Market Intelligence cache?'}`)) return;
                        try {
                          const token = await auth.currentUser?.getIdToken();
                          const adminToken = sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token');
                          const headers: Record<string, string> = {
                            'Content-Type': 'application/json'
                          };
                          if (token) headers['Authorization'] = `Bearer ${token}`;
                          if (adminToken) headers['X-Admin-Authorization'] = `Bearer ${adminToken}`;

                          const res = await fetch('/api/admin/force-refresh', {
                            method: 'POST',
                            headers,
                            credentials: 'include',
                            body: JSON.stringify({ categoryId: 'all' })
                          }).catch(() => null);
                          
                          if (!res?.ok) {
                            console.warn('Backend cache force-refresh API failed or returned error, proceeding with local refresh.');
                          }
                          
                          if (onFetchIntelligence) onFetchIntelligence(true);
                        } catch (e) {
                          console.error("Force refresh failed", e);
                        }
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-purple-500/20 transition-all font-mono"
                    >
                      <RefreshCcw className="w-3 h-3" />
                      {lang === 'ko' ? '캐시 갱신' : 'REFRESH'}
                    </button>
                  )}
                </>
              )}

              {(isLoading || isQuickScanLoading) && onStopAnalysis && (
                <button
                  onClick={onStopAnalysis}
                  className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-red-500/20 transition-all font-mono"
                >
                  <X className="w-3 h-3" />
                  {lang === 'ko' ? '분석 중단' : 'STOP'}
                </button>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          {(isLoading || isQuickScanLoading) && (
            <div className="w-full bg-white/5 border-b border-white/10 shrink-0">
              <div className="p-4 flex flex-col gap-3">
                <div className="flex justify-between items-center px-1">
                  <div className="flex items-center gap-3">
                    <div className={`w-1.5 h-1.5 rounded-full ${analysisStage === 1 ? 'bg-blue-400 animate-pulse' : 'bg-blue-400/20'}`} />
                    <span className={`text-[10px] uppercase tracking-widest font-black ${analysisStage === 1 ? 'text-white' : 'text-white/20'}`}>
                      {lang === 'ko' ? '시장 체제 분석 중 (Regime)' : 'REGIME ANALYSIS'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] uppercase tracking-widest font-black ${analysisStage === 2 ? 'text-white' : 'text-white/20'}`}>
                      {isQuickScanLoading ? 'SUMMARIZING...' : 'DATA MINING'}
                    </span>
                    <div className={`w-1.5 h-1.5 rounded-full ${analysisStage === 2 || isQuickScanLoading ? 'bg-purple-400 animate-pulse' : 'bg-purple-400/20'}`} />
                    <span className="text-[10px] font-mono font-bold text-blue-400 ml-2">
                      {Math.round(analysisProgress)}%
                    </span>
                  </div>
                </div>
                <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden relative">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: isQuickScanLoading ? '100%' : `${analysisProgress}%` }}
                    className={`h-full shadow-[0_0_15px_rgba(59,130,246,0.5)] rounded-full ${isQuickScanLoading ? 'bg-blue-400 animate-shimmer' : 'bg-blue-500'}`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Progressive Sub-navigation Tabs */}
          <div className="flex items-center gap-1 px-4 md:px-6 py-2.5 bg-black/40 border-b border-white/5 overflow-x-auto">
            <button
              onClick={() => setMacroSubTab('overview')}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                macroSubTab === 'overview'
                  ? 'bg-blue-600/30 text-blue-300 border border-blue-500/40 shadow-[0_0_12px_rgba(59,130,246,0.2)]'
                  : 'text-white/40 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              <Zap className="w-3.5 h-3.5" />
              <span>{lang === 'ko' ? '시장 퀵스캔 및 요약' : 'Quick Scan & Summary'}</span>
            </button>
            <button
              onClick={() => {
                setMacroSubTab('deep');
                setIsDetailedVisible(true);
              }}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                macroSubTab === 'deep'
                  ? 'bg-purple-600/30 text-purple-300 border border-purple-500/40 shadow-[0_0_12px_rgba(168,85,247,0.2)]'
                  : 'text-white/40 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>{lang === 'ko' ? '심층 인텔리전스 리포트' : 'Deep Intelligence'}</span>
            </button>
            <button
              onClick={() => setMacroSubTab('regime')}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap xl:hidden cursor-pointer ${
                macroSubTab === 'regime'
                  ? 'bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 shadow-[0_0_12px_rgba(16,185,129,0.2)]'
                  : 'text-white/40 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              <Shield className="w-3.5 h-3.5" />
              <span>{lang === 'ko' ? '시장 체제 (Regimes)' : 'Market Regimes'}</span>
            </button>
          </div>

          <div className={`p-4 md:p-8 space-y-6 md:space-y-10 flex-1 overflow-y-auto custom-scrollbar active:cursor-grab ${macroSubTab === 'overview' ? 'pb-8 md:pb-12' : 'pb-20 md:pb-32'}`}>
            {macroSubTab === 'regime' && (
              <div className="xl:hidden">
                <MacroRegimesCard
                  macro={macro}
                  indicators={indicators}
                  language={lang}
                  marketMode={marketMode}
                  mode="items"
                  noCard={false}
                  isLoading={isLoading || isQuickScanLoading}
                />
              </div>
            )}

            {/* Stage 1: Quick Scan Summary (Overview Tab) */}
            {macroSubTab === 'overview' && (
              <section className="bg-gradient-to-br from-blue-500/5 to-transparent border border-white/5 rounded-2xl md:rounded-[2rem] p-4 md:p-8 relative overflow-hidden transition-all duration-500 animate-fade-in">
                <div className="absolute top-0 right-0 p-8 opacity-5">
                  <Zap className="w-24 h-24 text-blue-400" />
                </div>

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 md:mb-8">
                  <div className="flex items-center gap-4 flex-wrap">
                    <h3 className="flex items-center gap-2 text-[10px] font-black text-blue-400 uppercase tracking-[0.3em]">
                      <Zap className="w-4 h-4 fill-current" />
                      {lang === 'ko' ? '시장 퀵스캔' : 'EXECUTIVE QUICK SCAN'}
                    </h3>
                    <span className="text-[10px] font-mono text-white/40 bg-white/5 px-2 py-0.5 rounded-md border border-white/10 hidden sm:inline-block">
                      {lang === 'ko' ? '기준 시각: ' : 'Base Time: '}
                      {(() => {
                        const dateVal = macro?.aiAnalysisDate || asOfDate || macro?.asOfDate;
                        if (!dateVal) return lang === 'ko' ? '분석 중...' : 'Analyzing...';
                        try {
                          return new Date(dateVal).toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US');
                        } catch {
                          return dateVal;
                        }
                      })()}
                    </span>
                    <button
                      onClick={() => setIsQuickScanExpanded(!isQuickScanExpanded)}
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-white/10 text-white/40 hover:text-blue-400 hover:bg-blue-500/10 hover:border-blue-500/20 transition-all shadow-lg active:scale-95 cursor-pointer"
                      title={isQuickScanExpanded ? (lang === 'ko' ? '접기' : 'Collapse') : (lang === 'ko' ? '펼치기' : 'Expand')}
                    >
                      <ChevronDown className={`w-5 h-5 transition-transform duration-500 ease-in-out ${isQuickScanExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {onGenerateQuickSummary && (
                      <button
                        onClick={() => onGenerateQuickSummary(true)}
                        disabled={isQuickScanLoading || isLoading}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 hover:border-blue-500/40 text-[11px] font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ml-1 cursor-pointer"
                        title={lang === 'ko' ? '시장 퀵스캔 재분석' : 'Refresh Quick Scan'}
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isQuickScanLoading ? 'animate-spin' : ''}`} />
                        <span>{lang === 'ko' ? '재분석' : 'Refresh'}</span>
                      </button>
                    )}
                  </div>
                  {mood && (
                    <div className="flex items-center gap-2 px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full">
                      <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest italic">{lang === 'ko' ? '시장 분위기' : 'MARKET MOOD'}</span>
                      <div className="w-px h-3 bg-white/10" />
                      <span className="text-sm font-bold text-white tracking-tight">{mood}</span>
                    </div>
                  )}
                </div>

                <motion.div
                  initial={false}
                  animate={{ height: isQuickScanExpanded ? 'auto' : '0px', opacity: isQuickScanExpanded ? 1 : 0 }}
                  className="overflow-hidden"
                >
                  <div className="text-white/80 leading-snug text-[14px] whitespace-pre-wrap pr-1">
                    {macro?.aiAnalysisError && !isQuickScanLoading ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <AlertTriangle className="w-8 h-8 text-red-500 mb-3" />
                        <p className="text-sm font-bold text-red-400 mb-1">{lang === 'ko' ? 'AI 분석 중 오류 발생' : 'AI Analysis Error'}</p>
                        <p className="text-xs text-white/50">{macro.aiAnalysisError}</p>
                      </div>
                    ) : cleanSummary ? (
                      renderFormattedSection(cleanSummary, compactSummaryBullets, false)
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-white/40">
                        {isQuickScanLoading ? (
                          <>
                            <ScannerPulse />
                            <p className="text-xs uppercase tracking-widest font-black mt-4 italic">
                              {Math.round(analysisProgress)}%
                            </p>
                          </>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Activity className="w-8 h-8 mb-4 opacity-50" />
                            <p className="text-sm font-medium mb-4">
                              {lang === 'ko' ? '분석된 리포트가 없습니다.' : 'No report available.'}
                            </p>
                            {onGenerateQuickSummary && (
                              <button
                                onClick={() => onGenerateQuickSummary(true)}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full text-sm font-bold shadow-lg shadow-blue-500/20 transition-all active:scale-95 flex items-center gap-2 cursor-pointer"
                              >
                                <Activity className="w-4 h-4" />
                                {lang === 'ko' ? '시장 퀵스캔 실행' : 'Run Quick Scan'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Pathway to Deep Report */}
                <div className="pt-4 mt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <span className="text-xs text-white/40 font-medium">
                    {lang === 'ko' ? '실시간 뉴스 센티먼트 및 기관급 심층 데이터 분석이 필요하신가요?' : 'Looking for deep institutional data mining?'}
                  </span>
                  <button
                    onClick={() => {
                      setMacroSubTab('deep');
                      setIsDetailedVisible(true);
                      if (!deepAnalysis) onFetchIntelligence?.(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 border border-purple-500/30 transition-all cursor-pointer shadow-sm active:scale-95"
                  >
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    <span>{lang === 'ko' ? '심층 인텔리전스 리포트 열기 →' : 'Open Deep Intelligence →'}</span>
                  </button>
                </div>
              </section>
            )}

            {/* Deep Intelligence Tab */}
            {macroSubTab === 'deep' && (
              <div className="space-y-6 animate-fade-in">
                {/* Stage 3: Deep Intelligence Action Gate */}
                {!isDetailedVisible && (
                  <motion.div
                    className="py-16 flex flex-col items-center justify-center space-y-8 bg-zinc-900/50 rounded-[3rem] border border-white/5 relative overflow-hidden"
                  >
                    <div className="text-center space-y-3 relative z-10">
                      <div className="flex justify-center mb-4">
                        <div className="p-4 bg-blue-500/10 rounded-2xl border border-blue-500/20">
                          <Globe className="w-8 h-8 text-blue-400 animate-pulse" />
                        </div>
                      </div>
                      <h4 className="text-2xl font-black text-white italic tracking-tighter uppercase">Deep Macro Intelligence</h4>
                      <p className="text-sm text-white/30 max-w-sm mx-auto font-medium px-8 text-center">
                        {lang === 'ko' ? "데이터 마이닝 및 실시간 뉴스 센티먼트가 포함된 기관급 분석 리포트를 생성합니다." : "Generates an institutional-grade report."}
                      </p>
                    </div>

                    <div className="flex justify-center w-full max-w-sm px-8">
                      <button
                        onClick={(isLoading || isQuickScanLoading) ? undefined : () => {
                          setIsDetailedVisible(true);
                          setIsQuickScanExpanded(false);
                          if (!deepAnalysis) onFetchIntelligence?.(true);
                        }}
                        disabled={isLoading || isQuickScanLoading || (isQuickScanLoading && !cleanSummary)}
                        className={`w-full flex items-center justify-center gap-4 px-8 py-5 rounded-2xl font-black text-sm uppercase tracking-[0.2em] transition-all group overflow-hidden h-[64px] cursor-pointer ${(isLoading || isQuickScanLoading || (isQuickScanLoading && !cleanSummary)) ? 'bg-blue-500/20 text-blue-300' : deepAnalysis ? 'bg-zinc-800' : 'bg-blue-600'
                          }`}
                      >
                        {(isLoading || isQuickScanLoading) ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                        <span>
                          {isLoading ? (lang === 'ko' ? '인텔리전스 분석 중...' : 'ANALYZING...') :
                            isQuickScanLoading ? (lang === 'ko' ? '시장 퀵스캔 진행 중...' : 'RUNNING QUICK SCAN...') :
                              deepAnalysis ? (lang === 'ko' ? '기존 리포트 보기' : 'VIEW LATEST REPORT') :
                                (lang === 'ko' ? '새 분석 실행' : 'RUN NEW ANALYSIS')}
                        </span>
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Stage 4: Detailed Analysis Rendering */}
                {isDetailedVisible && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-10">
                    {(isLoading || isQuickScanLoading) && !cleanDeepAnalysis && !macro?.aiIntelligenceError ? (
                      <div className="flex flex-col items-center justify-center py-40 text-white/20 gap-6">
                        <ScannerPulse />
                        <p className="text-[10px] uppercase tracking-[0.3em] font-black animate-pulse">SYNTHESIZING DEEP INTELLIGENCE...</p>
                      </div>
                    ) : macro?.aiIntelligenceError && !isLoading ? (
                      <div className="bg-[#0c0c0c] border border-white/5 rounded-2xl md:rounded-[2.5rem] p-4 md:p-10 shadow-2xl relative overflow-hidden flex flex-col items-center justify-center py-20 text-center">
                        <AlertTriangle className="w-10 h-10 text-red-500 mb-4" />
                        <p className="text-sm font-bold text-red-400 mb-2">{lang === 'ko' ? 'AI 인텔리전스 분석 중 오류 발생' : 'AI Intelligence Error'}</p>
                        <p className="text-xs text-white/50 mb-6">{macro.aiIntelligenceError}</p>
                        <button
                          onClick={() => { setIsDetailedVisible(false); setIsQuickScanExpanded(true); setMacroSubTab('overview'); }}
                          className="text-[10px] text-blue-400 font-bold uppercase tracking-widest border-b border-blue-400/20 pb-1 cursor-pointer"
                        >
                          {lang === 'ko' ? '대시보드로 돌아가기' : 'Return to Dashboard'}
                        </button>
                      </div>
                    ) : cleanDeepAnalysis ? (
                      <div className="bg-[#0c0c0c] border border-white/5 rounded-2xl md:rounded-[2.5rem] p-4 md:p-10 shadow-2xl relative overflow-hidden">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-8 gap-4">
                          <div className="flex items-center gap-4 flex-wrap">
                            <h4 className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em]">Deep Strategic Report</h4>
                            <span className="text-[9px] text-blue-400/60 font-mono bg-blue-400/5 px-2 py-0.5 rounded-md border border-blue-400/10 hidden sm:inline-block">
                              {lang === 'ko' ? '생성일시: ' : 'Generated At: '}
                              {(() => {
                                const dateVal = macro?.aiAnalysisDate || asOfDate || macro?.asOfDate;
                                if (!dateVal) return lang === 'ko' ? '분석 중...' : 'Analyzing...';
                                try {
                                  return new Date(dateVal).toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US');
                                } catch {
                                  return dateVal;
                                }
                              })()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => { setIsDetailedVisible(false); setIsQuickScanExpanded(true); setMacroSubTab('overview'); }}
                              className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-white/10 text-white/40 hover:text-blue-400 hover:bg-blue-500/10 hover:border-blue-500/20 transition-all shadow-lg active:scale-95 cursor-pointer"
                              title={lang === 'ko' ? '기본 리포트(퀵스캔)로 돌아가기' : 'Back to Executive Summary'}
                            >
                              <ChevronDown className="w-5 h-5 transition-transform duration-500 ease-in-out rotate-180" />
                            </button>
                            {onFetchIntelligence && (
                              <button
                                onClick={() => onFetchIntelligence(true)}
                                disabled={isLoading}
                                className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 hover:border-blue-500/40 text-[11px] font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ml-1 cursor-pointer"
                                title={lang === 'ko' ? '거시 전략 인텔리전스 재분석' : 'Refresh Intelligence'}
                              >
                                <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                                <span>{lang === 'ko' ? '재분석' : 'Refresh'}</span>
                              </button>
                            )}
                          </div>
                        </div>
                        {renderFormattedSection(cleanDeepAnalysis, compactDeepBullets, true)}
                      </div>
                    ) : (
                      // Fallback: If not loading and no data, show the action gate again instead of a blank screen
                      <div className="flex flex-col items-center justify-center py-20 text-white/20 italic">
                        <p className="text-xs uppercase tracking-widest font-black mb-4">No report data available</p>
                        <button
                          onClick={() => { setIsDetailedVisible(false); setIsQuickScanExpanded(true); setMacroSubTab('overview'); }}
                          className="text-[10px] text-blue-400 font-bold uppercase tracking-widest border-b border-blue-400/20 pb-1 cursor-pointer"
                        >
                          {lang === 'ko' ? '기본 리포트로 돌아가기' : 'Return to Dashboard'}
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Macro Regime Card on the Right (MMO-095) */}
        <div className="hidden xl:block h-full">
          <MacroRegimesCard
            macro={macro}
            indicators={indicators}
            language={lang}
            marketMode={marketMode}
            mode="items"
            noCard={false}
            isLoading={isLoading || isQuickScanLoading}
          />
        </div>
      </div>
    </div>
  );
}

// 3. Portfolio Allocation Chart
export function PortfolioChart({ holdings, language = 'ko' }: { holdings: Holding[], language?: string }) {
  const lang = language || 'ko';
  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

  return (
    <div className="bg-white/5 border border-white/10 p-6 rounded-3xl">
      <h3 className="font-semibold mb-6 flex items-center gap-2">
        <PieChart className="w-5 h-5 text-emerald-400" />
        {lang === 'ko' ? '자산 배분' : 'Asset Allocation'}
      </h3>
      <div className="flex-1 min-h-[256px] w-full relative min-w-0">
        <ResponsiveContainer width="100%" height={256} minWidth={0} minHeight={0}>
          <RePieChart>
            <Pie
              data={holdings}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              paddingAngle={5}
              dataKey="weight"
              nameKey="symbol"
            >
              {holdings.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
              itemStyle={{ color: '#fff' }}
              formatter={(value: number) => `${(value * 100).toFixed(1)}%`}
            />
          </RePieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-3 mt-4 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
        {holdings.map((h, i) => (
          <div key={h.symbol} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              <span className="text-white/60">{h.symbol}</span>
            </div>
            <span className="font-medium">{(h.weight * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}




// 5. Proposed Trades
export function ProposedTradesCard({
  trades,
  isLoading,
  language = 'ko',
  onRunAnalysis,
  marketMode = 'US',
  onSimulate
}: {
  trades: MarketSignal[],
  isLoading?: boolean,
  language?: string,
  onRunAnalysis?: () => void,
  marketMode?: 'US' | 'KR' | 'ALL',
  onSimulate?: () => void
}) {
  const lang = language || 'ko';
  return (
    <div className="bg-white/5 border border-white/10 p-6 rounded-3xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold flex items-center gap-2">
          <ArrowUpRight className="w-5 h-5 text-emerald-400" />
          {lang === 'ko' ? '목표 비중 편차 시뮬레이션' : 'Target Weight Deviation Simulation'}
          <TooltipHelp text={lang === 'ko' ? "거시 경제 리스크 대비 모의 포트폴리오의 비중 편차를 조절하기 위한 시뮬레이션 내역입니다." : "Simulated adjustments to align portfolio weights against macro risks."} />
        </h3>
        {onSimulate && (
          <button
            onClick={onSimulate}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 rounded-xl text-xs font-bold transition-all shadow-sm"
          >
            <span>⚡</span>
            {lang === 'ko' ? '시뮬레이션 실행' : 'Simulate'}
          </button>
        )}
      </div>
      <div className="space-y-4 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 text-white/20">
            <RefreshCw className="w-6 h-6 animate-spin mb-3 text-emerald-400" />
            <p className="text-xs uppercase tracking-widest font-mono italic">{lang === 'ko' ? '비중 편차 계산 중...' : 'CALCULATING WEIGHT DEVIATIONS...'}</p>
          </div>
        ) : trades.length > 0 ? (
          trades.map((trade, idx) => (
            <div key={`${trade.symbol}-${trade.portfolioAction}-${idx}`} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5 hover:border-white/10 transition-all">
              <div className="flex items-center gap-4 w-full">
                <div className={`p-3 rounded-xl flex-shrink-0 ${trade.portfolioAction === 'EXIT' || trade.portfolioAction === 'REDUCE' ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                  {trade.portfolioAction === 'EXIT' || trade.portfolioAction === 'REDUCE' ? <TrendingDown className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-bold text-white text-base">{(trade.symbol === 'CASH' || trade.symbol === 'KRW') ? (lang === 'ko' ? 'KRW (현금)' : 'KRW (Cash)') : trade.symbol}</div>
                    <div className="text-right font-mono text-xs flex flex-col items-end">
                      <div className="font-bold text-white tracking-tight">
                        {trade.proposedTradeShares > 0 ? '+' : ''}{trade.proposedTradeShares} {lang === 'ko' ? '주' : 'Shares'}
                      </div>
                      <div className="text-[10px] text-white/40">
                        {lang === 'ko' ? '추정 가치' : 'Est. Value'}: {formatCurrency(Math.abs(trade.proposedTradeShares) * trade.price, marketMode || 'US')}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <div className={`text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest ${trade.portfolioAction === 'EXIT' || trade.portfolioAction === 'REDUCE' ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                      {trade.portfolioAction === 'EXIT'
                        ? (lang === 'ko' ? '비중 제외' : 'Exclude Weight')
                        : trade.portfolioAction === 'REDUCE'
                          ? (lang === 'ko' ? '비중 축소' : 'Reduce Weight')
                          : (lang === 'ko' ? '비중 확대' : 'Increase Weight')}
                    </div>
                    <div className="flex items-center gap-2">
                      {trade.buyTargetPrice && trade.portfolioAction === 'ADD' && (
                        <div className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-lg border border-emerald-500/20 uppercase tracking-tight">
                          {lang === 'ko' ? '편입 기준가' : 'REF ADD'}: {formatCurrency(trade.buyTargetPrice, marketMode || 'US')}
                        </div>
                      )}
                      {(trade.portfolioAction === 'REDUCE' || trade.portfolioAction === 'EXIT') && trade.sellTargetPrice && (
                        <div className="text-[9px] font-bold text-red-400 bg-red-500/10 px-2 py-0.5 rounded-lg border border-red-500/20 uppercase tracking-tight">
                          {lang === 'ko' ? '축소 기준가' : 'REF REDUCE'}: {formatCurrency(trade.sellTargetPrice, marketMode || 'US')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rationale removed as per request - available in full report */}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-8 bg-white/[0.02] rounded-2xl border border-white/5 border-dashed">
            <div className="text-center py-4 text-white/20 italic text-sm space-y-1">
              <div>{lang === 'ko' ? "현재 조정이 필요한 비중 편차 내역이 없습니다." : "No weight deviation adjustments needed."}</div>
              <div className="text-[10px] opacity-60 not-italic">
                {lang === 'ko' ? "전략 생성 후 목록이 표시됩니다." : "The list will be displayed after strategy generation."}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// 6. New Asset Recommendations Grid
export function NewRecommendationsGrid({
  aiAdvice,
  isLoading,
  language = 'ko',
  onRunAnalysis,
  marketMode = 'US',
  onSimulate
}: {
  aiAdvice?: string,
  isLoading?: boolean,
  language?: string,
  onRunAnalysis?: () => void,
  marketMode?: 'US' | 'KR' | 'ALL',
  onSimulate?: (recommendations: any[]) => void
}) {
  const lang = language || 'ko';
  const [activeTrend, setActiveTrend] = React.useState<{
    id: string;
    label: string;
    unit: string;
    color: string;
    rect: DOMRect | null;
    currentValue?: number;
    frequency?: string;
  } | null>(null);

  const handleTrendClick = (
    e: React.MouseEvent,
    id: string,
    label: string,
    unit: string,
    color: string,
    currentValue?: number
  ) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveTrend({ id, label, unit, color, rect, currentValue });
  };

  // Robust parsing logic supporting both bullet lists and markdown tables
  const extractRecommendations = (text: string) => {
    const markers = ['신규 자산 추천', 'New Asset Recommendations', 'New Recommendations', '추천 종목', '신규 편입'];
    let sectionText = '';
    const lowerText = text.toLowerCase();
    for (const marker of markers) {
      if (lowerText.includes(marker.toLowerCase())) {
        const parts = text.split(new RegExp(`### .*?${marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*?\\n`, 'i'));
        if (parts.length > 1) {
          sectionText = parts[1].split('###')[0];
          break;
        } else {
          const altSplit = text.split(new RegExp(`${marker}`, 'i'));
          if (altSplit.length > 1) {
            sectionText = altSplit[1].split('###')[0];
            break;
          }
        }
      }
    }
    if (!sectionText) sectionText = text;

    const lines = sectionText.split('\n');
    const parsedRecs: { ticker: string, name: string, weight: string, buy: string, sell: string, reason: string }[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('|-') || trimmed.includes('Ticker') || trimmed.includes('종목') || trimmed.includes('Asset Name')) continue;

      // Check markdown table format: | Ticker | Name | Weight | Buy | Rationale |
      if (trimmed.startsWith('|')) {
        const cols = trimmed.split('|').map(c => c.trim()).filter(Boolean);
        if (cols.length >= 3) {
          const ticker = cols[0].replace(/\*\*/g, '').trim().toUpperCase();
          const name = cols[1].replace(/\*\*/g, '').trim();
          const weightStr = cols[2].replace(/\*\*/g, '').trim();
          const buyRaw = cols[3] ? cols[3].replace(/\*\*/g, '').trim() : 'N/A';
          const reason = cols[4] ? cols[4].replace(/\*\*/g, '').trim() : '';

          const formatPrice = (p: string) => {
            const raw = p.trim();
            if (!raw || raw.toLowerCase() === 'n/a') return 'N/A';
            if (raw.startsWith('₩') || raw.startsWith('$')) return raw;
            if (/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(raw) || isNaN(parseFloat(raw.replace(/[^0-9.-]/g, '')))) return raw;
            return marketMode === 'KR' ? `₩${raw}` : `$${raw}`;
          };

          if (ticker && ticker.length <= 15 && !ticker.includes('---')) {
            parsedRecs.push({
              ticker,
              name,
              weight: weightStr,
              buy: formatPrice(buyRaw),
              sell: 'N/A',
              reason
            });
          }
        }
      }
      // Check bullet list format
      else if (trimmed.startsWith('-') || trimmed.startsWith('*')) {
// eslint-disable-next-line
        const match = trimmed.match(/(?:\*|-)\s*(?:\**([A-Z0-9.\-_]+)\**)\s*[:\/-]?\s*(?:([^|]+))\s*\|\s*([^|]+)\s*\|\s*([^|]+)(?:\|\s*(.*))?/i);
        if (match) {
          const ticker = match[1].trim().toUpperCase();
          const name = match[2].trim().replace(/^\*\*|\*\*$/g, '');
          const weightStr = match[3].trim();
          const formatPrice = (p: string) => {
            const raw = p.trim();
            if (!raw || raw.toLowerCase() === 'n/a') return 'N/A';
            if (raw.startsWith('₩') || raw.startsWith('$')) return raw;
            if (/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(raw) || isNaN(parseFloat(raw.replace(/[^0-9.-]/g, '')))) return raw;
            return marketMode === 'KR' ? `₩${raw}` : `$${raw}`;
          };
          const buyStr = formatPrice(match[4]);
          const sellStr = 'N/A';
          const reason = (match[5] || '').trim();
          if (ticker && ticker.length <= 15) {
            parsedRecs.push({ ticker, name, weight: weightStr, buy: buyStr, sell: sellStr, reason });
          }
        }
      }
    }
    return parsedRecs;
  };

  const recommendations = extractRecommendations(aiAdvice || '');

  return (
    <div className="bg-white/5 border border-white/10 p-6 rounded-3xl h-full">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-400" />
          {lang === 'ko' ? '매크로 레짐별 관심 자산 배분' : 'Macro Regime Asset Allocation'}
          <TooltipHelp text={lang === 'ko' ? "현재 시장 국면에서 통계적 정합성이 높은 섹터 및 개별 자산의 모의 편입 분석입니다." : "Simulated allocation analysis of sectors and assets aligned with the current macro regime."} />
        </h3>
        {onSimulate && (
          <button
            onClick={() => onSimulate(recommendations)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 rounded-xl text-xs font-bold transition-all shadow-sm"
          >
            <span>⚡</span>
            {lang === 'ko' ? '시뮬레이션 실행' : 'Simulate'}
          </button>
        )}
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 text-white/20">
            <RefreshCw className="w-6 h-6 animate-spin mb-3 text-blue-400" />
            <p className="text-xs uppercase tracking-widest font-mono italic">{lang === 'ko' ? '신규 자산 분석 중...' : 'ANALYZING NEW ASSETS...'}</p>
          </div>
        ) : recommendations.length > 0 ? (
          recommendations.map((rec, i) => (
            <div
              key={i}
              className="group/rec cursor-pointer flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5 hover:border-blue-500/30 hover:bg-white/[0.08] transition-all"
              onClick={(e) => handleTrendClick(e, rec.ticker, rec.ticker, '$', '#60a5fa')}
            >
              <div className="flex items-center gap-4 text-left">
                <div className="p-3 rounded-xl bg-blue-500/10 text-blue-400 group-hover/rec:bg-blue-500/20 transition-colors">
                  <Target className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-bold text-white flex items-center gap-2">
                    {rec.ticker}
                    <LineChart className="w-3 h-3 text-white/20 group-hover/rec:text-blue-400 transition-colors" />
                  </div>
                  <div className="text-[10px] text-white/40 font-medium truncate max-w-[120px]">{rec.name}</div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-1">
                <div className="text-right">
                  <div className="text-xs font-black text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-lg border border-blue-500/20">
                    {rec.weight}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-lg border border-emerald-500/20 uppercase tracking-tight">
                    {lang === 'ko' ? '기준가' : 'REF'}: {rec.buy}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-8 bg-white/[0.02] rounded-2xl border border-white/5 border-dashed">
            <div className="text-center py-4 text-white/20 italic text-sm space-y-1">
              <div>{lang === 'ko' ? "분석된 관심 후보 자산이 없습니다." : "No candidate assets available."}</div>
              <div className="text-[10px] opacity-60 not-italic">
                {lang === 'ko' ? "전략 생성 후 목록이 표시됩니다." : "The list will be displayed after strategy generation."}
              </div>
            </div>
          </div>
        )}
      </div>

      {activeTrend && (
        <MacroTrendPopover
          indicatorId={activeTrend.id}
          label={activeTrend.label}
          unit={activeTrend.unit}
          color={activeTrend.color}
          isOpen={!!activeTrend}
          onClose={() => setActiveTrend(null)}
          triggerRect={activeTrend.rect}
          currentValue={activeTrend.currentValue}
          frequency={activeTrend.frequency}
        />
      )}
    </div>
  );
}

export function IntegratedStrategyGrid({ strategies, language = 'ko' }: { strategies?: string[], language?: string }) {
  if (!strategies || strategies.length === 0) return null;

  const getIcon = (index: number) => {
    switch (index % 3) {
      case 0: return <TrendingUp className="w-5 h-5 text-emerald-400" />;
      case 1: return <Target className="w-5 h-5 text-blue-400" />;
      case 2: return <Shield className="w-5 h-5 text-amber-400" />;
      default: return <Zap className="w-5 h-5 text-purple-400" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0a0a0a]/80 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden flex flex-col shadow-2xl"
    >
      <div className="p-6 border-b border-white/10 bg-gradient-to-r from-purple-900/20 to-transparent flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-500/10 rounded-xl border border-purple-500/20">
            <Lightbulb className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {language === 'ko' ? '통합 매크로 투자 전략' : 'Integrated Macro Strategy'}
            </h2>
            <p className="text-xs text-white/40 uppercase tracking-widest font-bold">
              {language === 'ko' ? '빅데이터 분석 기반' : 'Big Data Analytics Base'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 gap-4">
        {strategies.map((strategy, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="flex items-start gap-4 p-5 bg-white/[0.03] border border-white/5 rounded-2xl hover:bg-white/[0.05] transition-all group"
          >
            <div className="p-2.5 bg-white/5 rounded-lg group-hover:scale-110 transition-transform">
              {getIcon(idx)}
            </div>
            <div className="flex-1">
              <p className="text-sm text-white/80 leading-relaxed font-semibold">
                {strategy}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="px-6 py-4 bg-white/[0.02] border-t border-white/5 flex items-center justify-between">
        <span className="text-[10px] text-white/30 uppercase tracking-widest font-bold font-mono">
          Updated: {new Date().toLocaleDateString()}
        </span>
        <div className="flex gap-1">
          {[1, 2, 3].map(i => <div key={i} className="w-1 h-1 rounded-full bg-purple-500/30" />)}
        </div>
      </div>
    </motion.div>
  );
}

// 7. Trend Recommendations Widget (MMO-103)
export function TrendRecommendationsWidget({
  language = 'ko',
  marketMode = 'US',
  watchlistSymbols = [],
  onToggleWatchlist,
  defaultMentionFilter = '5PLUS'
}: {
  language?: string;
  marketMode?: 'US' | 'KR' | 'ALL';
  watchlistSymbols?: string[];
  onToggleWatchlist?: (symbol: string, name?: string, market?: string) => Promise<void> | void;
  defaultMentionFilter?: 'ALL' | '5PLUS';
}) {
  const isKo = language === 'ko';
  const [selectedMarket, setSelectedMarket] = React.useState<'US' | 'KR' | 'ALL'>(marketMode || 'US');
  const [mentionFilter, setMentionFilter] = React.useState<'ALL' | '5PLUS'>(defaultMentionFilter);
  const [recs, setRecs] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);
  const [togglingSymbol, setTogglingSymbol] = React.useState<string | null>(null);
  const [showAllRecs, setShowAllRecs] = React.useState(false);
  const INITIAL_VISIBLE_COUNT = 6;

  // 상위 글로벌 증시 모드 변경 시 동기화
  React.useEffect(() => {
    if (marketMode) {
      setSelectedMarket(marketMode);
    }
  }, [marketMode]);

  // 필터 변경 시 목록 접기 상태로 리셋
  React.useEffect(() => {
    setShowAllRecs(false);
  }, [selectedMarket, mentionFilter]);

  const fetchRecs = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedMarket && selectedMarket !== 'ALL') {
        params.append('market', selectedMarket);
      }
      if (mentionFilter === '5PLUS') {
        params.append('minMentions', '5');
      }
      const queryParam = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`/api/rebalance/trend-recommendations${queryParam}`);
      if (!res.ok) throw new Error('Failed to fetch trend recommendations');
      const json = await res.json() as any;
      if (json.success && json.data) {
        setRecs(json.data);
      } else {
        throw new Error(json.error || 'Failed to fetch recommendations');
      }
    } catch (err: any) {
      console.error('Error fetching trend recommendations:', err);
      setError(err.message || 'Error loading trends');
    } finally {
      setLoading(false);
    }
  }, [selectedMarket, mentionFilter]);

  React.useEffect(() => {
    fetchRecs();
  }, [fetchRecs]);

  const getActionBadgeColor = (action: string) => {
    const act = (action || '').toUpperCase();
    if (act.includes('STRONG BUY') || act.includes('HIGH CONFLUENCE')) return 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-300 border-emerald-400/40 shadow-sm shadow-emerald-500/10';
    if (act.includes('BUY') || act.includes('ADD') || act.includes('MOMENTUM')) return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    if (act.includes('SELL') || act.includes('REDUCE') || act.includes('EXIT') || act.includes('CAUTION')) return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
    if (act.includes('HOLD') || act.includes('NEUTRAL')) return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    return 'bg-white/5 text-white/40 border-white/10';
  };

  const formatActionLabel = (action: string) => {
    const act = (action || '').toUpperCase();
    if (act.includes('STRONG BUY')) return 'HIGH CONFLUENCE';
    if (act.includes('BUY') || act.includes('ADD')) return 'MOMENTUM';
    if (act.includes('SELL') || act.includes('REDUCE') || act.includes('EXIT')) return 'CAUTION';
    if (act.includes('HOLD')) return 'NEUTRAL';
    return action;
  };

  const getSentimentColor = (score: number) => {
    if (score > 3.0) return 'text-emerald-400 font-bold';
    if (score < -3.0) return 'text-rose-400 font-bold';
    return 'text-white/60';
  };

  const getConfluenceColor = (score: number) => {
    if (score >= 80) return 'text-emerald-300 font-black';
    if (score >= 65) return 'text-cyan-400 font-bold';
    if (score >= 50) return 'text-blue-400 font-bold';
    return 'text-white/60';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0a0a0a]/80 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden flex flex-col shadow-2xl"
    >
      {/* Header */}
      <div className="p-6 border-b border-white/10 bg-gradient-to-r from-blue-900/20 to-transparent flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-500/10 rounded-xl border border-blue-500/20">
            <Sparkles className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-white">
                {isKo ? '실시간 뉴스 트렌드 급상승 종목' : 'Live News Trend Mentions'}
              </h2>
              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                Multi-Signal Confluence
              </span>
              {recs.length > 0 && (
                <span className="text-[10px] font-bold text-white/50 px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                  {isKo ? `총 ${recs.length}개` : `${recs.length} stocks`}
                </span>
              )}
            </div>
            <p className="text-xs text-white/40 uppercase tracking-widest font-bold flex items-center gap-1.5 mt-0.5">
              <span>{isKo ? '뉴스 센티먼트(40%) + 옵션/공매도 수급(30%) + 퀀트 모멘텀(30%) 결합 분석' : 'News Sentiment (40%) + Options/Short (30%) + Quant Momentum (30%)'}</span>
            </p>
          </div>
        </div>

        {/* Right side controls: Market switcher tabs, Mention filter & Refresh */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* Mention Filter Toggle */}
          <div className="flex items-center bg-black/40 border border-white/10 p-1 rounded-xl backdrop-blur-md">
            <button
              onClick={() => setMentionFilter('ALL')}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                mentionFilter === 'ALL'
                  ? 'bg-blue-500/25 text-blue-300 border border-blue-500/40 shadow-sm'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              {isKo ? '전체 종목' : 'All'}
            </button>
            <button
              onClick={() => setMentionFilter('5PLUS')}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${
                mentionFilter === '5PLUS'
                  ? 'bg-amber-500/25 text-amber-300 border border-amber-500/40 shadow-sm'
                  : 'text-white/40 hover:text-white/70'
              }`}
              title={isKo ? '뉴스 언급 5회 이상 급상승 종목만 보기' : 'Filter stocks with 5+ mentions'}
            >
              <span>🔥</span>
              <span>{isKo ? '언급 5회↑ 급상승' : '5+ Mentions'}</span>
            </button>
          </div>

          {/* Market Switcher */}
          <div className="flex items-center bg-black/40 border border-white/10 p-1 rounded-xl backdrop-blur-md">
            <button
              onClick={() => setSelectedMarket('US')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                selectedMarket === 'US'
                  ? 'bg-blue-500/25 text-blue-300 border border-blue-500/40 shadow-sm'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              🇺🇸 {isKo ? '미국' : 'US'}
            </button>
            <button
              onClick={() => setSelectedMarket('KR')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                selectedMarket === 'KR'
                  ? 'bg-blue-500/25 text-blue-300 border border-blue-500/40 shadow-sm'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              🇰🇷 {isKo ? '한국' : 'KR'}
            </button>
            <button
              onClick={() => setSelectedMarket('ALL')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                selectedMarket === 'ALL'
                  ? 'bg-blue-500/25 text-blue-300 border border-blue-500/40 shadow-sm'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              🌐 {isKo ? '전체' : 'All'}
            </button>
          </div>

          <button
            onClick={fetchRecs}
            disabled={loading}
            className="p-2 bg-white/5 hover:bg-white/10 text-white/60 border border-white/10 rounded-xl transition-all active:scale-95 disabled:opacity-40"
            title={isKo ? '새로고침' : 'Refresh'}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-white/30">
            <RefreshCw className="w-8 h-8 animate-spin mb-3 text-blue-400" />
            <p className="text-sm font-semibold tracking-wider font-mono uppercase">{isKo ? '멀티 시그널 Confluence 분석 및 지표 산출 중...' : 'COMPILING CONFLUENCE METRICS...'}</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500/80 mb-3" />
            <p className="text-white/80 font-semibold mb-2">{isKo ? '트렌드 데이터를 불러오지 못했습니다.' : 'Failed to load trend data'}</p>
            <p className="text-xs text-white/40 mb-4">{error}</p>
            <button
              onClick={fetchRecs}
              className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 rounded-xl text-xs font-bold transition-all"
            >
              {isKo ? '재시도' : 'Try Again'}
            </button>
          </div>
        ) : recs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-white/30">
            <Info className="w-8 h-8 mb-2 text-white/20" />
            <p className="text-sm font-semibold">
              {mentionFilter === '5PLUS'
                ? (isKo ? '최근 48시간 내 뉴스 언급 5회 이상 종목이 없습니다. "전체 종목" 탭에서 수급/모멘텀 포착 종목을 확인하세요.' : 'No stocks with 5+ mentions found. Switch to "All" to view momentum trends.')
                : (isKo
                  ? `최근 분석된 ${selectedMarket === 'US' ? '미국(US)' : selectedMarket === 'KR' ? '한국(KR)' : ''} 긍정적 트렌드 종목이 없습니다.`
                  : `No recent trending stocks found${selectedMarket !== 'ALL' ? ` for ${selectedMarket}` : ''}.`)}
            </p>
            <p className="text-xs mt-1 text-white/20">{isKo ? '스케줄러 작업(worker-scheduler)이 완료된 후 트렌드 지표가 집계됩니다.' : 'Trend metrics compile via worker-scheduler.'}</p>
          </div>
        ) : (() => {
          const visibleRecs = showAllRecs ? recs : recs.slice(0, INITIAL_VISIBLE_COUNT);
          const hasMore = recs.length > INITIAL_VISIBLE_COUNT;

          return (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
                {visibleRecs.map((rec, idx) => {
                  const isExpanded = expandedIndex === idx;
                  const confScore = rec.confluenceScore || 0;
                  const drivers: string[] = Array.isArray(rec.trendDrivers) ? rec.trendDrivers : [];
                  const isWatchlisted = watchlistSymbols.some(s => s.toUpperCase() === rec.symbol.toUpperCase());
                  const isHotTrend = (rec.mentionCount || 0) >= 5;

                  return (
                    <div
                      key={rec.symbol}
                      className="bg-white/[0.02] border border-white/5 hover:border-blue-500/20 rounded-2xl p-5 hover:bg-white/[0.04] transition-all flex flex-col h-full group"
                    >
                      {/* Top row: symbol, market, confluence badge and action */}
                      <div className="flex items-start justify-between mb-3 gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-base font-black text-white group-hover:text-blue-400 transition-colors">
                              {rec.name && rec.name !== rec.symbol ? rec.name : rec.symbol}
                            </span>
                            {rec.name && rec.name !== rec.symbol && (
                              <span className="text-[10px] font-bold font-mono px-1.5 py-0.5 rounded bg-white/5 text-white/40 border border-white/5">
                                {rec.symbol}
                              </span>
                            )}
                            <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-white/5 text-white/40 border border-white/5">
                              {rec.market}
                            </span>
                            {isHotTrend ? (
                              <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30 flex items-center gap-1">
                                <span>🔥</span>
                                <span>{isKo ? '언급 급상승' : 'Hot Trend'}</span>
                              </span>
                            ) : (
                              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30">
                                {isKo ? '수급 복합' : 'Multi-Signal'}
                              </span>
                            )}
                            {confScore > 0 && (
                              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                                <Zap className="w-2.5 h-2.5 text-cyan-400" />
                                <span className="text-[9px] font-black font-mono text-cyan-300">
                                  {confScore}pt
                                </span>
                              </div>
                            )}
                          </div>
                          {rec.name && rec.name !== rec.symbol ? (
                            <h4 className="text-[11px] text-white/40 font-medium truncate max-w-[200px] mt-0.5">
                              {rec.market === 'KR' ? '한국거래소(KRX)' : 'US Market'} · {rec.symbol}
                            </h4>
                          ) : (
                            <h4 className="text-xs text-white/50 font-semibold truncate max-w-[160px] mt-0.5">
                              {rec.name}
                            </h4>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {onToggleWatchlist && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (togglingSymbol) return;
                                setTogglingSymbol(rec.symbol);
                                try {
                                  await onToggleWatchlist(rec.symbol, rec.name, rec.market);
                                } finally {
                                  setTogglingSymbol(null);
                                }
                              }}
                              disabled={togglingSymbol === rec.symbol}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold transition-all active:scale-95 ${
                                isWatchlisted
                                  ? 'bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25'
                                  : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-amber-500/30 hover:bg-white/10'
                              }`}
                              title={
                                isWatchlisted
                                  ? (isKo ? '관심종목 해제' : 'Remove from Watchlist')
                                  : (isKo ? '관심종목 추가' : 'Add to Watchlist')
                              }
                            >
                              {togglingSymbol === rec.symbol ? (
                                <RefreshCw className="w-3 h-3 animate-spin text-amber-400" />
                              ) : (
                                <Star
                                  className={`w-3 h-3 ${
                                    isWatchlisted
                                      ? 'text-amber-400 fill-amber-400'
                                      : 'text-white/40 group-hover:text-amber-400'
                                  }`}
                                />
                              )}
                              <span>
                                {isWatchlisted
                                  ? (isKo ? '관심종목' : 'Saved')
                                  : (isKo ? '+ 관심종목' : '+ Watch')}
                              </span>
                            </button>
                          )}
                          <span className={`text-[10px] font-black px-2.5 py-1 rounded-lg border ${getActionBadgeColor(rec.recommendedAction)}`}>
                            {formatActionLabel(rec.recommendedAction)}
                          </span>
                        </div>
                      </div>

                      {/* Trend drivers badges */}
                      {drivers.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {drivers.map((driver, dIdx) => (
                            <span
                              key={dIdx}
                              className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-300 border border-blue-500/15 flex items-center gap-1"
                            >
                              <span className="w-1 h-1 rounded-full bg-blue-400 inline-block" />
                              {driver}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Quantitative signals grid */}
                      <div className="grid grid-cols-3 gap-2 bg-black/30 border border-white/5 rounded-xl p-3 mb-4 text-center">
                        <div>
                          <div className="text-[9px] text-white/30 font-bold uppercase tracking-wider">{isKo ? '뉴스 언급' : 'News Mentions'}</div>
                          <div className={`text-sm font-mono font-black mt-0.5 ${isHotTrend ? 'text-rose-400 font-extrabold' : 'text-white'}`}>
                            {rec.mentionCount}회
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] text-white/30 font-bold uppercase tracking-wider">{isKo ? '센티먼트' : 'Sentiment'}</div>
                          <div className={`text-sm font-mono font-black mt-0.5 ${getSentimentColor(rec.sentimentScore)}`}>
                            {rec.sentimentScore > 0 ? `+${rec.sentimentScore}` : rec.sentimentScore}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] text-white/30 font-bold uppercase tracking-wider">{isKo ? 'Confluence' : 'Confluence'}</div>
                          <div className={`text-sm font-mono font-black mt-0.5 ${getConfluenceColor(confScore)}`}>
                            {confScore > 0 ? `${confScore}점` : 'N/A'}
                          </div>
                        </div>
                      </div>

                      {/* Rationale description */}
                      <p className="text-xs text-white/70 leading-relaxed font-medium mb-4 flex-1">
                        {rec.trendRationale}
                      </p>

                      {/* Outbound news links */}
                      {rec.sourceNews && rec.sourceNews.length > 0 && (
                        <div className="border-t border-white/5 pt-3 mt-auto">
                          <button
                            onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                            className="flex items-center justify-between w-full text-[10px] text-white/40 hover:text-white/60 font-black uppercase tracking-wider transition-colors"
                          >
                            <span>{isKo ? `출처 뉴스 분석 (${rec.sourceNews.length})` : `Source News (${rec.sourceNews.length})`}</span>
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden space-y-2 mt-2 pt-1"
                              >
                                {rec.sourceNews.map((news: any, nIdx: number) => (
                                  <a
                                    key={nIdx}
                                    href={news.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start gap-1.5 p-2 bg-white/[0.02] hover:bg-white/[0.04] border border-white/5 rounded-xl text-[11px] text-white/50 hover:text-white/80 transition-all group/link"
                                  >
                                    <span className="text-[10px] opacity-30 mt-0.5 font-bold font-mono">#{nIdx + 1}</span>
                                    <span className="flex-1 leading-snug line-clamp-2">
                                      {news.titleKo || news.titleEn}
                                    </span>
                                    <ArrowUpRight className="w-3.5 h-3.5 opacity-30 group-hover/link:opacity-80 transition-opacity mt-0.5 flex-shrink-0" />
                                  </a>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* View More / Show Less Toggle Bar */}
              {hasMore && (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-white/5">
                  <span className="text-xs text-white/40 font-medium">
                    {isKo
                      ? (showAllRecs
                          ? `전체 ${recs.length}개 급상승 포착 종목을 모두 표시하고 있습니다.`
                          : `상위 ${INITIAL_VISIBLE_COUNT}개 급상승 종목을 표시 중입니다. (미표시 +${recs.length - INITIAL_VISIBLE_COUNT}개)`)
                      : (showAllRecs
                          ? `Showing all ${recs.length} trending stocks.`
                          : `Showing top ${INITIAL_VISIBLE_COUNT} of ${recs.length} trending stocks.`)}
                  </span>
                  <button
                    onClick={() => setShowAllRecs(!showAllRecs)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-white/5 hover:bg-blue-500/20 active:scale-95 text-blue-300 hover:text-blue-200 border border-white/10 hover:border-blue-500/40 transition-all shadow-sm"
                  >
                    <span>
                      {showAllRecs
                        ? (isKo ? '목록 접기 (상위 6개만 보기)' : 'Show Less (Top 6)')
                        : (isKo ? `급상승 종목 전체보기 (+${recs.length - INITIAL_VISIBLE_COUNT}개 더보기)` : `View All (+${recs.length - INITIAL_VISIBLE_COUNT} more)`)}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${showAllRecs ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Footer info */}
      <div className="px-6 py-4 bg-white/[0.02] border-t border-white/5 flex items-center justify-between">
        <span className="text-[10px] text-white/30 uppercase tracking-widest font-bold font-mono">
          {isKo ? '매크로 & 트렌드 리밸런싱 모듈 v1.0' : 'Macro & Trend Rebalancing Module v1.0'}
        </span>
        <div className="flex gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[9px] text-emerald-400 font-bold uppercase tracking-wider">{isKo ? '실시간 동기화됨' : 'Synced'}</span>
        </div>
      </div>
    </motion.div>
  );
}

/** 2대 증권사(한국투자 · LS) 통합 잔고 동기화 & 실시간 미체결 트래킹 위젯 */
export function ConsolidatedBrokerageWidget({
  accounts = [],
  language = 'ko',
  onSyncComplete
}: {
  accounts?: any[];
  language?: string;
  onSyncComplete?: () => void;
}) {
  const isKo = language === 'ko';
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [activeTab, setActiveTab] = useState<'accounts' | 'pending' | 'ls_options'>('accounts');

  // LS Options & Short Selling state
  const [selectedSymbol, setSelectedSymbol] = useState('005930');
  const [optionsData, setOptionsData] = useState<any[]>([]);
  const [shortSellingData, setShortSellingData] = useState<any>(null);
  const [isLoadingLsData, setIsLoadingLsData] = useState(false);

  const loadPending = async () => {
    setIsLoadingPending(true);
    try {
      const res = await fetchPendingOrders();
      setPendingOrders(res.orders || []);
    } catch (e) {
      console.warn('[ConsolidatedWidget] Failed to load pending orders', e);
    } finally {
      setIsLoadingPending(false);
    }
  };

  const [cooldown, setCooldown] = useState(0);

  const handleSyncAll = async (forceManual = true) => {
    if (cooldown > 0 && forceManual) {
      setSyncStatus(isKo ? `${cooldown}초 후 다시 동기화할 수 있습니다.` : `Please wait ${cooldown}s before re-syncing.`);
      setTimeout(() => setSyncStatus(null), 3000);
      return;
    }

    setIsSyncing(true);
    setSyncStatus(isKo ? '증권사 자산 스마트 동기화 중...' : 'Smart syncing brokerages...');
    try {
      const res = await syncAllBrokerages({ force: forceManual });
      if (res.success) {
        if (res.cached) {
          setSyncStatus(isKo ? '최신 잔고 캐시 유지 중 (장중 30분 보호)' : 'Recent cache verified (protected)');
        } else {
          setSyncStatus(isKo ? '실시간 동기화 완료!' : 'Real-time Sync Completed!');
        }
        if (forceManual) {
          setCooldown(30);
          const interval = setInterval(() => {
            setCooldown((prev) => {
              if (prev <= 1) {
                clearInterval(interval);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
        await loadPending();
        if (onSyncComplete) onSyncComplete();
      }
    } catch (err: any) {
      setSyncStatus(isKo ? `동기화 실패: ${err.message}` : `Sync failed: ${err.message}`);
    } finally {
      setIsSyncing(false);
      setTimeout(() => setSyncStatus(null), 4000);
    }
  };

  const loadLsData = async (symbol: string) => {
    setIsLoadingLsData(true);
    try {
      const [optRes, shortRes] = await Promise.all([
        fetchLsOptionsChain(symbol).catch(() => ({ options: [] })),
        fetchLsShortSelling(symbol).catch(() => (null))
      ]);
      setOptionsData(optRes.options || []);
      setShortSellingData(shortRes);
    } catch (err) {
      console.warn('[ConsolidatedWidget] Failed to load LS data', err);
    } finally {
      setIsLoadingLsData(false);
    }
  };

  return (
    <div className="glass-premium rounded-3xl p-6 border border-white/10 space-y-6 bg-gradient-to-br from-white/[0.03] to-purple-500/[0.02]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/5 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
            <Database className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              {isKo ? '국내 주요 증권사 통합 멀티 브로커 모듈' : 'Consolidated Multi-Broker Module'}
              <span className="px-2 py-0.5 rounded-md bg-blue-500/20 text-blue-400 text-[10px] font-extrabold border border-blue-500/30 uppercase tracking-widest">
                KIS · LS
              </span>
            </h3>
            <p className="text-white/40 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{isKo ? '한국투자증권 · LS증권 통합 자산 동기화 & 실시간 주문 트래커' : 'Unified Asset Sync & Real-time Order Tracker'}</span>
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 text-[10px] font-bold border border-emerald-500/20">
                {isKo ? '⚡ 스마트 하이브리드 동기화' : '⚡ Smart Hybrid Sync'}
              </span>
            </p>
          </div>
        </div>

        <button
          onClick={() => handleSyncAll(true)}
          disabled={isSyncing || cooldown > 0}
          className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
        >
          <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing
            ? (isKo ? '동기화 진행 중...' : 'Syncing...')
            : cooldown > 0
              ? (isKo ? `${cooldown}초 대기` : `Wait ${cooldown}s`)
              : (isKo ? '실시간 강제 동기화' : 'Force Sync All')}
        </button>
      </div>

      {syncStatus && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-xs text-blue-300 font-medium flex items-center justify-between animate-fade-in">
          <span>{syncStatus}</span>
          <span className="text-[10px] font-mono text-white/30">{new Date().toLocaleTimeString()}</span>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-white/5 pb-2">
        <button
          onClick={() => setActiveTab('accounts')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'accounts'
              ? 'bg-white/10 text-white border border-white/10'
              : 'text-white/40 hover:text-white hover:bg-white/5'
          }`}
        >
          {isKo ? '📊 증권사별 계좌 잔고' : 'Accounts Balance'} ({accounts.length})
        </button>

        <button
          onClick={() => {
            setActiveTab('pending');
            loadPending();
          }}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'pending'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
              : 'text-white/40 hover:text-white hover:bg-white/5'
          }`}
        >
          {isKo ? '⏳ 미체결 주문 트래커' : 'Pending Orders'}
          {pendingOrders.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-amber-500 text-black text-[10px] font-extrabold">
              {pendingOrders.length}
            </span>
          )}
        </button>

        <button
          onClick={() => {
            setActiveTab('ls_options');
            loadLsData(selectedSymbol);
          }}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'ls_options'
              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
              : 'text-white/40 hover:text-white hover:bg-white/5'
          }`}
        >
          {isKo ? '📈 LS 옵션/공매도 분석' : 'LS Options & Short'}
        </button>
      </div>

      {/* Tab 1: Connected Accounts */}
      {activeTab === 'accounts' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {['kis', 'ls'].map(provider => {
            const accList = accounts.filter(a => a.provider === provider);
            const providerName = provider === 'kis' ? '한국투자증권' : 'LS증권';
            const colorClass = provider === 'kis' ? 'from-blue-500/10 to-sky-500/5 border-blue-500/20' : 'from-purple-500/10 to-indigo-500/5 border-purple-500/20';
            const totalBalance = accList.reduce((sum, a) => sum + (a.balance || 0), 0);

            return (
              <div key={provider} className={`p-4 rounded-2xl bg-gradient-to-br ${colorClass} border space-y-3`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-white uppercase tracking-wider">{providerName}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold ${accList.length > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white/40'}`}>
                    {accList.length > 0 ? (isKo ? '연동됨' : 'Active') : (isKo ? '미연동' : 'Inactive')}
                  </span>
                </div>

                <div>
                  <div className="text-[10px] text-white/40 font-bold uppercase">{isKo ? '총 예수금 잔고' : 'Total Cash Balance'}</div>
                  <div className="text-lg font-mono font-black text-white mt-0.5">
                    {formatCurrency(totalBalance, 'KR')}
                  </div>
                </div>

                <div className="text-[10px] text-white/40">
                  {accList.length} {isKo ? '개 계좌 동기화 완료' : 'Account(s) synced'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tab 2: Pending Orders Tracker */}
      {activeTab === 'pending' && (
        <div className="space-y-3">
          {isLoadingPending ? (
            <div className="flex items-center justify-center py-8 text-xs text-white/40">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              {isKo ? '미체결 주문목록 조회 중...' : 'Loading pending orders...'}
            </div>
          ) : pendingOrders.length === 0 ? (
            <div className="text-center py-8 text-xs text-white/40 bg-white/[0.02] rounded-2xl border border-white/5">
              {isKo ? '현재 진행 중인 미체결 주문이 없습니다.' : 'No active pending orders.'}
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
              {pendingOrders.map((ord, idx) => (
                <div key={ord.id || idx} className="p-3 bg-white/5 rounded-xl border border-white/5 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${ord.provider === 'kis' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                      {ord.provider}
                    </span>
                    <div>
                      <div className="font-black text-white flex items-center gap-2">
                        {ord.symbol}
                        <span className={`text-[10px] ${ord.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'}`}>
                          {ord.side}
                        </span>
                      </div>
                      <div className="text-[10px] text-white/40">{ord.order_type} @ {ord.price ? ord.price.toLocaleString() : 'MARKET'}</div>
                    </div>
                  </div>
                  <div className="text-right font-mono">
                    <div className="font-bold text-white">{ord.quantity} SH</div>
                    <div className="text-[10px] text-amber-400">{ord.status}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 3: LS Options & Short Selling Analysis */}
      {activeTab === 'ls_options' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/60 font-bold">{isKo ? '종목 선택:' : 'Symbol:'}</span>
            {['005930', '000660', '035720', 'AAPL'].map(sym => (
              <button
                key={sym}
                onClick={() => {
                  setSelectedSymbol(sym);
                  loadLsData(sym);
                }}
                className={`px-3 py-1 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer ${
                  selectedSymbol === sym
                    ? 'bg-purple-500 text-white'
                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                }`}
              >
                {sym}
              </button>
            ))}
          </div>

          {isLoadingLsData ? (
            <div className="flex items-center justify-center py-8 text-xs text-white/40">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              {isKo ? 'LS 파생/공매도 데이터 조회 중...' : 'Fetching LS derivatives data...'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Options Chain Card */}
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5 space-y-3">
                <div className="text-xs font-bold text-purple-300 flex items-center justify-between">
                  <span>{isKo ? '옵션 체인 및 IV Rank' : 'Options Chain & IV Rank'}</span>
                  <span className="text-[10px] text-white/30 font-mono">{optionsData.length} Contracts</span>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                  {optionsData.slice(0, 5).map((opt, oIdx) => (
                    <div key={oIdx} className="p-2 bg-black/30 rounded-lg text-[11px] flex justify-between items-center font-mono">
                      <div>
                        <span className={opt.option_type === 'CALL' ? 'text-emerald-400 font-bold' : 'text-red-400 font-bold'}>
                          [{opt.option_type}]
                        </span>
                        <span className="ml-2 text-white/80">Strike: {opt.strike_price}</span>
                      </div>
                      <div className="text-white/60">
                        IV: {opt.implied_volatility || 20}% | Δ: {opt.delta || 0.5}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Short Selling Card */}
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5 space-y-3">
                <div className="text-xs font-bold text-cyan-300 flex items-center justify-between">
                  <span>{isKo ? '공매도 잔고 및 수량 추적' : 'Short Selling Tracker'}</span>
                  <span className="text-[10px] text-white/30 font-mono">LS TR t1923</span>
                </div>
                {shortSellingData?.shortVolumeHistory && shortSellingData.shortVolumeHistory.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar font-mono text-[11px]">
                    {shortSellingData.shortVolumeHistory.slice(0, 5).map((sv: any, sIdx: number) => (
                      <div key={sIdx} className="p-2 bg-black/30 rounded-lg flex justify-between items-center">
                        <span className="text-white/50">{sv.trade_date}</span>
                        <span className="text-white">Short Vol: {sv.short_volume?.toLocaleString()}</span>
                        <span className="text-cyan-400">Ratio: {((sv.short_ratio || 0) * 100).toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-white/40 py-6 text-center">
                    {isKo ? '공매도 데이터 수집 대기 중' : 'Short selling data pending collection'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

